import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import apcLogo from './assets/APC Logo.png';

// Define Firebase configuration
const firebaseConfig = {
    apiKey: "AIzaSyBqB0n2C_RakKIV-lnXy7MdpzHnsBTvA2c",
    // Must match the domain the app is served from so the Google sign-in
    // popup is same-origin — cross-origin authDomain breaks when browsers
    // block third-party cookies (popup closes, result never arrives).
    authDomain: "apc-signin.web.app",
    projectId: "apc-signin",
    storageBucket: "apc-signin.firebasestorage.app",
    messagingSenderId: "465019905574",
    appId: "1:465019905574:web:164a11a538d8312907a8e7",
    measurementId: "G-B9HJ1FR3TS"
};

const appId = firebaseConfig.appId;
const AppContext = createContext(null);

// Basic shape (the register does the real matching): 7-9 digits, an OPTIONAL letter,
// optional -NN suffix. The register accepts digits-only numbers (verified 23 Oct 2026:
// a digits-only card returned a CURRENT match), so the letter must not be required —
// requiring it locked out visitors whose cards have no letter. The debounce plus the
// 7-digit minimum still keeps partial-typing API calls down.
const WWCC_BASIC_PATTERN = /^\d{7,9}[A-Za-z]?(-\d{1,2})?$/;

// Today's date as YYYY-MM-DD in Melbourne time. toISOString() is UTC, which
// shows yesterday's date until ~10-11am AEST/AEDT.
const melbourneToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Melbourne' });

// Normalize an Australian mobile number to a canonical 04xxxxxxxx form so that
// "0412 345 678", "+61412345678" and "0412345678" all match as the same person
// for lookups, de-duplication and SMS. Falls back to the digit-stripped input.
const normalizeMobile = (raw) => {
    let s = (raw || '').replace(/[\s\-()]/g, '');
    if (s.startsWith('+61')) s = '0' + s.slice(3);
    else if (s.startsWith('61') && s.length === 11) s = '0' + s.slice(2);
    return s;
};

const formatVisitorType = (type) => {
    if (type === 'parentMeeting') return 'Parent Meeting';
    if (type === 'crt') return 'CRT';
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
};

// Define theme colors
const theme = {
    blue: '#96D6D3', // brand teal — decorative use (progress dots, rings, accents)
    blueDark: '#5FB6B2', // deeper teal for buttons/links so white text stays readable
    grey: '#6D6E71', // R:109 G:110 B:113
    yellow: '#FFD92B', // single yellow for all admin action buttons
    mediumGrey: '#E0E1E2', //
};

const CAMPUS_COLORS = { '83 Danks St': '#D1F2EB', '40 Bay St': '#D6EAF8', '120 Bay St': '#FCF3CF', '62 Pickles St': '#E8DAEF' };

// Friendly empty-state block for the admin views (live/history/search).
const EmptyState = ({ icon, title, subtitle }) => (
    <div className="mt-12 mb-10 text-center">
        <div className="text-4xl mb-3" aria-hidden="true" style={{ opacity: 0.5 }}>{icon}</div>
        <p className="font-semibold" style={{ color: theme.grey }}>{title}</p>
        {subtitle && <p className="text-sm mt-1" style={{ color: theme.grey, opacity: 0.65 }}>{subtitle}</p>}
    </div>
);

const AdminLoginPage = ({ onAdminLogin, message, onReturnHome }) => {
    const { theme } = useContext(AppContext);

    return (
        <div className="p-6 rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: 'white' }}>
            <h2 className="text-2xl font-semibold mb-4 text-center" style={{ color: theme.grey }}>Admin Portal Login</h2>
            <p className="text-center mb-6" style={{ color: theme.grey }}>Please sign in with your school Google account.</p>
            {message && <p className="text-center text-red-500 mb-4">{message}</p>}
            <button
                onClick={onAdminLogin}
                className="w-full px-4 py-3 text-white font-semibold rounded-lg shadow-md hover:opacity-90 flex items-center justify-center"
                style={{ backgroundColor: '#4285F4' }} // Google Blue
            >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l8.59 6.67C12.72 13.94 17.89 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v8.51h13.01c-.59 3.09-2.28 5.48-4.79 7.18l8.44 6.52C44.63 37.33 46.98 31.45 46.98 24.55z"></path>
                    <path fill="#FBBC05" d="M12.72 19.89C12.18 18.27 11.9 16.45 11.9 14.5c0-1.95.28-3.77.81-5.39L2.56 2.56C.97 5.61 0 9.82 0 14.5s.97 8.89 2.56 11.94l10.16-6.55z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-8.44-6.52c-2.13 1.42-4.84 2.27-7.45 2.27-6.11 0-11.28-4.44-13.14-10.38L2.56 35.44C6.51 43.62 14.62 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                Sign In with Google
            </button>
            <button
                onClick={onReturnHome}
                className="w-full mt-4 px-4 py-3 font-semibold rounded-lg shadow-md hover:bg-gray-50"
                style={{
                    backgroundColor: 'transparent',
                    color: theme.grey,
                    border: `1px solid ${theme.mediumGrey}`
                }}
            >
                Return to Visitor Sign In
            </button>
        </div>
    );
};

const App = () => {
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [functions, setFunctions] = useState(null);
    // WWCC checks run from an Australian region — the Vic Gov WAF blocks
    // US-datacenter traffic, which is where the default (us-central1) runs.
    const [functionsAu, setFunctionsAu] = useState(null);
    const [adminLoginMessage, setAdminLoginMessage] = useState('');
    const [initialCampus, setInitialCampus] = useState(null);
    const [pageMode, setPageMode] = useState('visitor'); // 'visitor' or 'admin'
    const refreshTimer = useRef();

    useEffect(() => {
        // --- REVISION: Set page mode based on path, and campus based on query param ---
        const path = window.location.pathname.toLowerCase();
        if (path === '/admin') {
            setPageMode('admin');
        } else {
            setPageMode('visitor');
        }

        const urlParams = new URLSearchParams(window.location.search);
        const campusParam = urlParams.get('campus');
        const campusMap = {
            '83danks': '83 Danks St',
            '40bay': '40 Bay St',
            '120bay': '120 Bay St',
            '62pickles': '62 Pickles St',
        };

        if (campusParam && campusMap[campusParam.toLowerCase()]) {
            setInitialCampus(campusMap[campusParam.toLowerCase()]);
        }
    }, []);

    useEffect(() => {
        const initializeFirebase = async () => {
            try {
                const app = initializeApp(firebaseConfig);
                const firestoreDb = getFirestore(app);
                const firebaseAuth = getAuth(app);
                const firebaseFunctions = getFunctions(app);

                setDb(firestoreDb);
                setAuth(firebaseAuth);
                setFunctions(firebaseFunctions);
                setFunctionsAu(getFunctions(app, 'australia-southeast1'));

                const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
                    if (user) {
                        setUserId(user.uid);
                        // If user is signed in with Google (not anonymous) and on admin page,
                        // auto-check admin status so they don't have to re-login on refresh
                        const isGoogleUser = user.providerData.some(p => p.providerId === 'google.com');
                        if (isGoogleUser && window.location.pathname.toLowerCase() === '/admin') {
                            try {
                                const checkAdmin = httpsCallable(firebaseFunctions, 'checkAdminStatus');
                                const result = await checkAdmin({ email: user.email });
                                if (result.data.isAdmin) {
                                    // Force-refresh ID token so the admin custom claim
                                    // set server-side is included in subsequent calls.
                                    await user.getIdToken(true);
                                    setIsAdmin(true);
                                }
                            } catch (err) {
                                console.error("Auto admin check failed:", err);
                            }
                        }
                    } else {
                        try {
                            await signInAnonymously(firebaseAuth);
                            setUserId(firebaseAuth.currentUser?.uid);
                        } catch (error) {
                            console.error("Error during anonymous sign-in:", error);
                            setUserId(crypto.randomUUID());
                        }
                    }
                    setIsAuthReady(true);
                });

                return () => unsubscribe();
            } catch (error) {
                console.error("Error initializing Firebase application:", error);
                setIsAuthReady(true);
            }
        };
        initializeFirebase();
    }, []);

    // Keep a ref of isAdmin so the auto-updater (below) can read it without
    // re-subscribing its listeners every time admin status changes.
    const isAdminRef = useRef(false);
    useEffect(() => { isAdminRef.current = isAdmin; }, [isAdmin]);

    // Kiosk auto-updater: iPads in kiosk mode are never manually refreshed, so
    // they'd run stale code indefinitely. Every 15 min we fetch index.html with
    // cache bypassed and compare the deployed main-bundle hash to the running
    // one; when a new version is live we reload — but only once the screen has
    // been idle for 60s, so a visitor is never interrupted mid sign-in. Admins
    // are left alone (they can use the in-portal reload button).
    useEffect(() => {
        const runningSrc = Array.from(document.querySelectorAll('script'))
            .map(s => s.src)
            .find(src => /\/static\/js\/main\.[a-z0-9]+\.js/.test(src));
        const runningHash = runningSrc ? (runningSrc.match(/main\.([a-z0-9]+)\.js/) || [])[1] : null;
        if (!runningHash) return; // dev server / unexpected layout — skip

        let updatePending = false;
        let lastActivity = Date.now();
        const bump = () => { lastActivity = Date.now(); };
        const activityEvents = ['touchstart', 'mousedown', 'keydown'];
        activityEvents.forEach(e => window.addEventListener(e, bump, { passive: true }));

        const checkVersion = async () => {
            try {
                const res = await fetch(`/?_=${Date.now()}`, { cache: 'no-store' });
                const html = await res.text();
                const m = html.match(/\/static\/js\/main\.([a-z0-9]+)\.js/);
                if (m && m[1] !== runningHash) updatePending = true;
            } catch (_) { /* offline — try again next interval */ }
        };

        const maybeReload = () => {
            if (updatePending && !isAdminRef.current && Date.now() - lastActivity > 60000) {
                window.location.reload();
            }
        };

        const versionTimer = setInterval(checkVersion, 15 * 60 * 1000);
        const reloadTimer = setInterval(maybeReload, 30 * 1000);
        return () => {
            clearInterval(versionTimer);
            clearInterval(reloadTimer);
            activityEvents.forEach(e => window.removeEventListener(e, bump));
        };
    }, []);

    const handleGoogleAdminLogin = async () => {
        if (!auth || !functions) return;
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const checkAdminStatus = httpsCallable(functions, 'checkAdminStatus');
            const adminResult = await checkAdminStatus({ email: user.email });

            if (adminResult.data.isAdmin) {
                // Force-refresh ID token so the admin custom claim set server-side
                // is included in subsequent admin function calls.
                await user.getIdToken(true);
                setIsAdmin(true);
            } else {
                setAdminLoginMessage("Access denied. You are not a member of the required group.");
                await signOut(auth);
            }
        } catch (error) {
            console.error("Error during admin sign-in:", error);
            if (error.code !== 'auth/popup-closed-by-user') {
                setAdminLoginMessage(`An error occurred during sign-in. Please try again. (${error.code || error.message})`);
            }
        }
    };

    const handlePressStart = () => {
        refreshTimer.current = setTimeout(() => {
            window.location.reload();
        }, 2000);
    };

    const handlePressEnd = () => {
        clearTimeout(refreshTimer.current);
    };

    // Full-screen loading gate: only the admin portal needs it (it covers the
    // auto-relogin check and stops the login page flashing). Kiosks render the
    // form immediately — anonymous auth completes in the background long before
    // a visitor could finish step 1, and final submit guards on userId anyway.
    const isAdminPath = window.location.pathname.toLowerCase() === '/admin';
    if (!db || !auth || !functions || (isAdminPath && (!isAuthReady || !userId))) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-xl font-semibold text-gray-700">Loading application...</div>
            </div>
        );
    }

    return (
        <AppContext.Provider value={{ db, auth, userId, appId, isAdmin, setIsAdmin, theme, functions, functionsAu }}>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet" />
            <div
                className="min-h-screen flex flex-col items-center justify-center p-4 font-sans relative"
                style={{
                    fontFamily: "'Inter', sans-serif",
                    backgroundImage: `url('/school_background.jpg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}></div>
                <div className="relative z-10 flex flex-col items-center justify-center w-full">
                    <img
                        src={apcLogo}
                        alt="Albert Park College Logo"
                        className="mb-6"
                        style={{ maxWidth: '200px', height: 'auto' }}
                        onError={(e) => { e.target.src = "https://placehold.co/200x100/E0C800/6D6E71?text=APC+Logo"; }}
                        onMouseDown={handlePressStart}
                        onMouseUp={handlePressEnd}
                        onMouseLeave={handlePressEnd}
                        onTouchStart={handlePressStart}
                        onTouchEnd={handlePressEnd}
                    />

                    {isAdmin ? (
                        <AdminPortal onLogout={() => { signOut(auth).catch(() => {}).finally(() => { setIsAdmin(false); window.location.href = '/admin'; }); }} />
                    ) : (
                        pageMode === 'admin' ? (
                            <AdminLoginPage onAdminLogin={handleGoogleAdminLogin} message={adminLoginMessage} onReturnHome={() => window.location.href = '/'} />
                        ) : (
                            <VisitorSignIn initialCampus={initialCampus} />
                        )
                    )}
                </div>
            </div>
        </AppContext.Provider>
    );
};

// ... The VisitorSignIn and AdminPortal components remain unchanged from the previous version ...
// I am including them in full below for completeness.

// Live VIT check status shown under the VIT number input, in both the CRT flow
// and the visitor-with-VIT flow. Mirrors the WWCC status boxes.
const VitCheckStatusDisplay = ({ status, result, lastName, vitNumber, theme }) => (
    <>
        {!lastName.trim() && vitNumber && (
            <p className="mt-2 text-xs text-amber-600">Enter your last name above so we can verify your VIT registration.</p>
        )}
        {status === 'checking' && (
            <p className="mt-2 text-sm" style={{ color: theme.grey }}>⏳ Verifying...</p>
        )}
        {status === 'success' && result && (
            <div className="mt-2 p-3 rounded-md bg-green-50 border border-green-200 text-sm">
                <p className="font-semibold text-green-800">✓ VIT registration found</p>
                {result.fullName && <p className="text-green-700">Registered teacher: {result.fullName}</p>}
                {result.registrationType && <p className="text-green-700">{result.registrationType}</p>}
                {result.expiryDate && <p className="text-green-700">Expires: {result.expiryDate}</p>}
                {result.surnameMatches === false && (
                    <p className="mt-1 text-amber-700">Note: the registered name differs from the name you entered — reception may confirm your identity.</p>
                )}
            </div>
        )}
        {status === 'failed' && result && (
            <div className="mt-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm">
                <p className="font-semibold text-red-800">✗ VIT registration could not be verified</p>
                <p className="text-red-700">
                    {!result.found && 'No registration was found for this number. Please check and try again.'}
                    {result.found && result.isExpired && 'This VIT registration has expired.'}
                    {result.found && !result.isExpired && 'Please see reception.'}
                </p>
            </div>
        )}
        {status === 'error' && (
            <div className="mt-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
                <p className="font-semibold text-amber-800">⚠ VIT register unreachable</p>
                <p className="text-amber-700">Sign-in is allowed but your VIT registration will be checked manually by reception.</p>
            </div>
        )}
    </>
);

const VisitorSignIn = ({ initialCampus }) => {
    const { userId, theme, functions, functionsAu } = useContext(AppContext);

    const volunteerPolicies = [
        { name: 'Volunteer Induction Pack', url: 'https://albertparkcollege.vic.edu.au/resources/volunteers-induction.pdf' },
        { name: 'Volunteers Policy', url: 'https://albertparkcollege.vic.edu.au/resources/12-03-25_APC-Volunteers-Policy.pdf' },
        { name: 'Child Safety and Wellbeing Policy', url: 'https://albertparkcollege.vic.edu.au/resources/23-08-24_APC-Child-Safety-and-Wellbeing-Policy%20.pdf' },
        { name: 'Child Safety Responding and Reporting Obligations Policy and Procedures', url: 'https://albertparkcollege.vic.edu.au/resources/23-08-24_Child-Safety-Responding-and-Reporting-Obligations-%28incl.-Mandatory-Reporting%29-Policy-and-Procedures.pdf' },
        { name: 'Child Safety Code of Conduct', url: 'https://albertparkcollege.vic.edu.au/resources/21-08-24_APC-Child-Safety-Code-of-Conduct.pdf' },
        { name: 'PROTECT: Identify Child Abuse', url: 'https://www.schools.vic.gov.au/identify-child-abuse?Redirect=1' },
        { name: 'PROTECT: Four Critical Actions for Schools – Responding to Incidents, Disclosures and Suspicions of Child Abuse', url: 'https://www.schools.vic.gov.au/report-child-abuse-schools?Redirect=1' },
    ];
    const contractorPolicies = [
        { name: 'Child Safety Code of Conduct', url: 'https://albertparkcollege.vic.edu.au/resources/21-08-24_APC-Child-Safety-Code-of-Conduct.pdf' },
        { name: 'Child Safety and Wellbeing Policy', url: 'https://albertparkcollege.vic.edu.au/resources/23-08-24_APC-Child-Safety-and-Wellbeing-Policy%20.pdf' }
    ];

    const [campus, setCampus] = useState(initialCampus || '');
    const [mobileNumber, setMobileNumber] = useState('');
    const [visitorType, setVisitorType] = useState('');
    const [purposeOfVisit, setPurposeOfVisit] = useState('');
    const [company, setCompany] = useState('');
    const [personVisiting, setPersonVisiting] = useState('');
    const [email, setEmail] = useState('');
    const [wwccNumber, setWwccNumber] = useState('');
    // Live-check state: 'idle' | 'checking' | 'success' | 'failed' | 'error'
    const [wwccCheckStatus, setWwccCheckStatus] = useState('idle');
    const [wwccCheckResult, setWwccCheckResult] = useState(null);
    const wwccCheckTimerRef = useRef(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [samsRegistered, setSamsRegistered] = useState(false);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmedVisitorType, setConfirmedVisitorType] = useState('');
    const [step, setStep] = useState(1);
    const [isAlreadySignedIn, setIsAlreadySignedIn] = useState(false);
    const [signedInDocId, setSignedInDocId] = useState(null);
    const [policiesAccepted, setPoliciesAccepted] = useState(false);
    const [sendPoliciesEmail, setSendPoliciesEmail] = useState(false);
    const [vitNumber, setVitNumber] = useState('');
    // Live-check state, mirroring the WWCC states: 'idle' | 'checking' | 'success' | 'failed' | 'error'
    const [vitCheckStatus, setVitCheckStatus] = useState('idle');
    const [vitCheckResult, setVitCheckResult] = useState(null);
    const vitCheckTimerRef = useRef(null);
    const vitPattern = /^\d{6}$/;
    const getVitValidationMsg = (val) => {
        if (!val) return '';
        const digitsOnly = val.replace(/\D/g, '');
        if (digitsOnly.length < 6) return 'VIT number must be 6 digits';
        if (digitsOnly.length > 6 || val.length > 6) return 'VIT number must be exactly 6 digits';
        return '';
    };
    const [checkType, setCheckType] = useState(''); // 'wwcc' or 'vit'

    // Debounced live WWCC check: fires when wwccNumber + lastName are populated
    useEffect(() => {
        if (wwccCheckTimerRef.current) {
            clearTimeout(wwccCheckTimerRef.current);
            wwccCheckTimerRef.current = null;
        }

        if (checkType !== 'wwcc' || !wwccNumber || wwccNumber.length < 7 || !lastName.trim() || !functionsAu) {
            setWwccCheckStatus('idle');
            setWwccCheckResult(null);
            return;
        }
        if (!WWCC_BASIC_PATTERN.test(wwccNumber)) {
            setWwccCheckStatus('idle');
            setWwccCheckResult(null);
            return;
        }

        setWwccCheckStatus('checking');
        wwccCheckTimerRef.current = setTimeout(async () => {
            try {
                const checkWwcc = httpsCallable(functionsAu, 'checkWwcc');
                const result = await checkWwcc({ cardNumber: wwccNumber, surname: lastName.trim() });
                const data = result.data || {};
                if (!data.success) {
                    setWwccCheckStatus('error');
                    setWwccCheckResult({ message: data.message || 'WWCC service unavailable.' });
                } else if (data.status === 'CURRENT' && !data.isExpired) {
                    setWwccCheckStatus('success');
                    setWwccCheckResult(data);
                } else {
                    setWwccCheckStatus('failed');
                    setWwccCheckResult(data);
                }
            } catch (err) {
                console.error('checkWwcc error:', err);
                setWwccCheckStatus('error');
                setWwccCheckResult({ message: 'Unable to reach WWCC service.' });
            }
        }, 1000);

        return () => {
            if (wwccCheckTimerRef.current) clearTimeout(wwccCheckTimerRef.current);
        };
    }, [wwccNumber, lastName, checkType, functionsAu]);

    // Debounced live VIT check: fires when a complete VIT number + last name are
    // populated for a CRT (or a visitor who chose VIT), mirroring the WWCC check
    useEffect(() => {
        if (vitCheckTimerRef.current) {
            clearTimeout(vitCheckTimerRef.current);
            vitCheckTimerRef.current = null;
        }

        const vitRequired = visitorType === 'crt' || checkType === 'vit';
        if (!vitRequired || !/^\d{6}$/.test(vitNumber) || !lastName.trim() || !functions) {
            setVitCheckStatus('idle');
            setVitCheckResult(null);
            return;
        }

        setVitCheckStatus('checking');
        vitCheckTimerRef.current = setTimeout(async () => {
            try {
                const checkVit = httpsCallable(functions, 'checkVit');
                const result = await checkVit({ registrationNumber: vitNumber, surname: lastName.trim() });
                const data = result.data || {};
                if (!data.success) {
                    setVitCheckStatus('error');
                    setVitCheckResult({ message: data.message || 'VIT register unavailable.' });
                } else if (data.found && !data.isExpired) {
                    // A surname mismatch is a soft warning, not a block — the admin
                    // portal flags "name differs!" on the record for reception.
                    setVitCheckStatus('success');
                    setVitCheckResult(data);
                } else {
                    setVitCheckStatus('failed');
                    setVitCheckResult(data);
                }
            } catch (err) {
                console.error('checkVit error:', err);
                setVitCheckStatus('error');
                setVitCheckResult({ message: 'Unable to reach the VIT register.' });
            }
        }, 600);

        return () => {
            if (vitCheckTimerRef.current) clearTimeout(vitCheckTimerRef.current);
        };
    }, [vitNumber, lastName, visitorType, checkType, functions]);

    const searchUserAndProceed = async () => {
        if (!functions || !mobileNumber || !campus || isSearching) return;
        setMessage('');
        // Canonicalize the number once, up front, so lookup/de-dupe/SMS all agree.
        const normalizedMobile = normalizeMobile(mobileNumber);
        if (!/^04\d{8}$/.test(normalizedMobile)) {
            setMessage('Please enter a valid Australian mobile number, e.g. 0412 345 678.');
            return;
        }
        if (normalizedMobile !== mobileNumber) setMobileNumber(normalizedMobile);
        setIsSearching(true);
        try {
            const lookupVisitorByMobile = httpsCallable(functions, 'lookupVisitorByMobile');
            const result = await lookupVisitorByMobile({ mobileNumber: normalizedMobile });

            if (result.data.found) {
                const userData = result.data.visitor;
                if (userData.firstName || userData.lastName) {
                    setFirstName(userData.firstName || '');
                    setLastName(userData.lastName || '');
                } else if (userData.name) {
                    // Best-effort split for legacy records: last word = surname
                    const parts = userData.name.trim().split(/\s+/);
                    setLastName(parts.length > 1 ? parts.pop() : '');
                    setFirstName(parts.join(' '));
                } else {
                    setFirstName(''); setLastName('');
                }
                setEmail(userData.email || '');
                setVisitorType(userData.visitorType || '');
                setCompany(userData.company || '');
                setPersonVisiting(userData.personVisiting || '');
                setWwccNumber(userData.wwccNumber || '');
                setSamsRegistered(userData.samsRegistered || false);
                setVitNumber(userData.vitNumber || '');
                setPurposeOfVisit(userData.purposeOfVisit || '');

                if (userData.vitNumber) setCheckType('vit');
                else if (userData.wwccNumber) setCheckType('wwcc');
                else setCheckType('');

                const greetName = userData.firstName || (userData.name ? userData.name.trim().split(/\s+/)[0] : '');
                setMessage(`Welcome back${greetName ? `, ${greetName}` : ''}! Please confirm your details to proceed.`);
            } else {
                setMessage('No previous records found. Please enter your details.');
                setFirstName(''); setLastName(''); setEmail(''); setVisitorType(''); setCompany(''); setPersonVisiting('');
                setWwccNumber(''); setSamsRegistered(false);
                setVitNumber(''); setPurposeOfVisit(''); setCheckType('');
            }
            setStep(2);
        } catch (error) {
            console.error("Error searching user:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. This kiosk must be on the school network.');
            } else {
                setMessage('Error searching for user. Please try again.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    const handleSignOutHere = async () => {
        if (!functions || !signedInDocId) return;
        try {
            const signOutVisitor = httpsCallable(functions, 'signOutVisitor');
            await signOutVisitor({ visitorId: signedInDocId });
            setMessage('You have been signed out. You can now sign in again.');
            setIsAlreadySignedIn(false);
            setSignedInDocId(null);
        } catch (error) {
            console.error("Error signing out:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. This kiosk must be on the school network.');
            } else {
                setMessage("Error signing out. Please contact reception.");
            }
        }
    };

    const proceedToPolicyStep = (e) => {
        e.preventDefault();
        setMessage('');
        if (!firstName.trim() || !lastName.trim()) {
            setMessage("Please enter both your first and last name.");
            return;
        }
        if (visitorType === 'volunteer' && !email.trim()) {
            setMessage("Email is required for volunteers — we'll send you the policy documents.");
            return;
        }
        if (!visitorType || (visitorType !== 'crt' && visitorType !== 'visitor' && !purposeOfVisit)) {
            setMessage("Please fill in all required details.");
            return;
        }

        const requiresCheck = ['contractor', 'volunteer', 'visitor'].includes(visitorType);
        if (requiresCheck) {
            if (!checkType) {
                setMessage("Please select either WWCC or VIT.");
                return;
            }
            if (checkType === 'wwcc') {
                if (!wwccNumber) {
                    setMessage("Please enter your WWCC Number.");
                    return;
                }
                if (wwccCheckStatus === 'checking') {
                    setMessage("Still verifying your WWCC, please wait...");
                    return;
                }
                if (wwccCheckStatus === 'failed') {
                    setMessage("Your WWCC could not be verified. Please check the number and surname, or see reception.");
                    return;
                }
                if (wwccCheckStatus === 'idle') {
                    setMessage("Please enter a valid WWCC number to verify.");
                    return;
                }
                // 'success' and 'error' (API down) are both allowed to proceed
            }
            if (checkType === 'vit' && !vitNumber) {
                setMessage("Please enter your VIT Number.");
                return;
            }
            if (checkType === 'vit' && vitNumber && !vitPattern.test(vitNumber)) {
                setMessage(getVitValidationMsg(vitNumber) || "VIT number must be exactly 6 digits.");
                return;
            }
        }

        if (visitorType === 'crt' && !vitNumber) {
            setMessage("Please enter your VIT Number.");
            return;
        }
        if (visitorType === 'crt' && vitNumber && !vitPattern.test(vitNumber)) {
            setMessage(getVitValidationMsg(vitNumber) || "VIT number must be exactly 6 digits.");
            return;
        }

        if ((visitorType === 'crt' || checkType === 'vit') && vitNumber) {
            if (vitCheckStatus === 'checking') {
                setMessage("Still verifying your VIT registration, please wait...");
                return;
            }
            if (vitCheckStatus === 'failed') {
                setMessage("Your VIT registration could not be verified. Please check the number, or see reception.");
                return;
            }
            // 'success' and 'error' (register down) are both allowed to proceed
        }

        if (['visitor', 'crt', 'parentMeeting'].includes(visitorType)) {
            handleFinalSubmit(e);
        } else {
            setStep(3);
        }
    };

    const handleFinalSubmit = async (e) => {
        e.preventDefault();
        if (!functions || !userId) return;
        setIsSubmitting(true);
        setMessage('');

        const finalPurposeOfVisit = visitorType === 'crt' ? 'Casual Relief Teaching' : purposeOfVisit;
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        const trimmedEmail = email.trim();
        const name = `${trimmedFirst} ${trimmedLast}`;

        const visitorData = {
            mobileNumber: normalizeMobile(mobileNumber), campus, name, firstName: trimmedFirst, lastName: trimmedLast,
            email: trimmedEmail, visitorType, purposeOfVisit: finalPurposeOfVisit,
            signOutTime: null,
            ...(company && { company }),
            ...(samsRegistered && { samsRegistered }),
            ...(personVisiting && { personVisiting }),
            ...(checkType === 'wwcc' && {
                wwccNumber,
                wwccExpiry: wwccCheckResult?.expiryDate || null,
                wwccStatus: wwccCheckStatus === 'success' ? 'current' : (wwccCheckStatus === 'error' ? 'unverified_api_down' : 'unknown'),
                wwccCardType: wwccCheckResult?.cardType || null,
                wwccCheckRef: wwccCheckResult?.transactionRef || null,
                wwccCheckedAt: wwccCheckResult?.checkedAt || null,
            }),
            ...(checkType === 'vit' && { vitNumber }),
            ...(visitorType === 'crt' && { vitNumber }),
            userId: userId
        };

        try {
            const createVisitor = httpsCallable(functions, 'createVisitor');
            const result = await createVisitor({ visitorData });

            if (result.data.alreadySignedIn) {
                setMessage('This mobile number is already signed in at this campus.');
                setIsAlreadySignedIn(true);
                setIsSubmitting(false);
                return;
            }

            const visitorId = result.data.visitorId;
            setConfirmedVisitorType(visitorType);
            setShowConfirmationModal(true);

            // Send SMS and policies email (fire-and-forget, but log failures so a
            // silently-dropped SMS/email leaves a trace in the console + Cloud Logging).
            httpsCallable(functions, 'sendSignInSms')({ mobileNumber: normalizeMobile(mobileNumber), campus, visitorId })
                .catch(err => console.error('sendSignInSms failed:', err));
            // Volunteers always receive policies; other types only if they opted in
            const shouldEmailPolicies = trimmedEmail && (visitorType === 'volunteer' || sendPoliciesEmail);
            if (shouldEmailPolicies) {
                httpsCallable(functions, 'sendPoliciesByEmail')({ email: trimmedEmail, visitorType })
                    .catch(err => console.error('sendPoliciesByEmail failed:', err));
            }

            setMobileNumber(''); setCampus(initialCampus || ''); setFirstName(''); setLastName(''); setEmail(''); setVisitorType('');
            setPurposeOfVisit(''); setCompany(''); setPersonVisiting(''); setWwccNumber('');
            setWwccCheckStatus('idle'); setWwccCheckResult(null);
            setSamsRegistered(false); setPoliciesAccepted(false);
            setSendPoliciesEmail(false); setVitNumber(''); setCheckType('');
            setStep(1);
        } catch (error) {
            console.error("Error signing in visitor:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. This kiosk must be on the school network.');
            } else {
                setMessage('Error signing in. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset the whole kiosk back to a clean step 1, so the next visitor never
    // inherits the previous person's data (used by idle timeout + modal close).
    const resetForm = () => {
        setMobileNumber(''); setCampus(initialCampus || ''); setFirstName(''); setLastName('');
        setEmail(''); setVisitorType(''); setPurposeOfVisit(''); setCompany(''); setPersonVisiting('');
        setWwccNumber(''); setWwccCheckStatus('idle'); setWwccCheckResult(null);
        setSamsRegistered(false); setPoliciesAccepted(false); setSendPoliciesEmail(false);
        setVitNumber(''); setCheckType(''); setIsAlreadySignedIn(false); setSignedInDocId(null);
        setMessage(''); setStep(1);
    };

    const closeModal = () => {
        setShowConfirmationModal(false);
        resetForm();
    };

    // Idle auto-reset: after 75s without interaction show an "Are you still
    // there?" warning, then 15s later clear the form so a walked-away kiosk
    // session doesn't expose stale data to the next visitor. Any tap dismisses
    // the warning (events bubble to the container's bumpIdle handlers).
    const idleTimerRef = useRef(null);
    const idleResetRef = useRef(null);
    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const bumpIdle = () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (idleResetRef.current) clearTimeout(idleResetRef.current);
        setShowIdleWarning(false);
        idleTimerRef.current = setTimeout(() => {
            // Only interrupt when there's something to lose — an untouched
            // step-1 form resets invisibly without nagging passers-by.
            if (step !== 1 || mobileNumber || showConfirmationModal) {
                setShowIdleWarning(true);
            }
            idleResetRef.current = setTimeout(() => {
                setShowIdleWarning(false);
                setShowConfirmationModal(false);
                resetForm();
            }, 15000);
        }, 75000);
    };
    useEffect(() => {
        bumpIdle();
        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (idleResetRef.current) clearTimeout(idleResetRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-close the success modal on the kiosk with a visible countdown.
    const [modalCountdown, setModalCountdown] = useState(25);
    useEffect(() => {
        if (!showConfirmationModal) return;
        setModalCountdown(25);
        const interval = setInterval(() => setModalCountdown(c => c - 1), 1000);
        return () => clearInterval(interval);
    }, [showConfirmationModal]);
    useEffect(() => {
        if (showConfirmationModal && modalCountdown <= 0) closeModal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalCountdown, showConfirmationModal]);

    return (
        <div className="p-6 rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: 'white' }} onMouseDown={bumpIdle} onKeyDown={bumpIdle} onTouchStart={bumpIdle}>
            <p className="text-xs font-semibold uppercase tracking-widest mb-1 text-center" style={{ color: theme.grey }}>Albert Park College</p>
            <h1 className="text-2xl font-semibold mb-3 text-center" style={{ color: theme.grey }}>Visitor Sign In</h1>
            {/* Kiosks are locked to a campus via URL hash — show it prominently so
                visitors can tell at a glance they're signing in at the right place. */}
            {initialCampus && (
                <p className="text-center mb-3">
                    <span className="px-3 py-1 text-sm font-semibold rounded-full" style={{ backgroundColor: CAMPUS_COLORS[initialCampus] || theme.mediumGrey, color: theme.grey }}>{initialCampus}</span>
                </p>
            )}

            {/* Step progress indicator (mobile → details → policies) */}
            <div className="flex items-center justify-center space-x-2 mb-4" aria-label={`Step ${step} of 3`}>
                {[1, 2, 3].map(n => (
                    <span key={n} className="h-2 rounded-full transition-all" style={{ width: n === step ? '28px' : '10px', backgroundColor: n <= step ? theme.blue : theme.mediumGrey }} />
                ))}
            </div>

            {message && !showConfirmationModal && (
                <div className="text-center mb-4">
                    <p className="text-sm px-3 py-2 rounded-lg inline-block" style={{ color: theme.grey, backgroundColor: '#F3F4F6' }}>{message}</p>
                    {isAlreadySignedIn && <button onClick={handleSignOutHere} className="mt-2 px-4 py-2 text-white font-semibold rounded-lg shadow-md" style={{ backgroundColor: theme.yellow }}>Sign Out</button>}
                </div>
            )}

            {step === 1 && (
                <div className="space-y-4">
                    <div>
                        <label htmlFor="mobile-number" className="block text-sm font-medium" style={{ color: theme.grey }}>Mobile Number</label>
                        <input type="tel" inputMode="numeric" id="mobile-number" name="apc-mobile" autoComplete="off" autoCorrect="off" spellCheck="false" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} required placeholder="e.g. 0412345678" />
                    </div>
                    <div>
                        <label htmlFor="campus" className="block text-sm font-medium" style={{ color: theme.grey }}>Campus</label>
                        <select id="campus" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={campus} onChange={(e) => { setCampus(e.target.value); if (e.target.value !== '62 Pickles St' && visitorType === 'tutor') setVisitorType(''); }} required disabled={!!initialCampus}>
                            <option value="">Select Campus</option>
                            <option value="83 Danks St">83 Danks St</option>
                            <option value="40 Bay St">40 Bay St</option>
                            <option value="120 Bay St">120 Bay St</option>
                            <option value="62 Pickles St">62 Pickles St</option>
                        </select>
                    </div>
                    <button onClick={searchUserAndProceed} className="w-full px-4 py-3 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: theme.blueDark }} disabled={!mobileNumber || !campus || isSearching}>{isSearching ? 'Searching...' : 'Continue'}</button>
                </div>
            )}

            {step === 2 && (
                <form onSubmit={proceedToPolicyStep} className="space-y-4" autoComplete="off">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="first-name" className="block text-sm font-medium" style={{ color: theme.grey }}>First Name</label>
                            <input type="text" id="first-name" name="apc-first" autoComplete="off" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="e.g. Jane" required />
                        </div>
                        <div>
                            <label htmlFor="last-name" className="block text-sm font-medium" style={{ color: theme.grey }}>Last Name</label>
                            <input type="text" id="last-name" name="apc-last" autoComplete="off" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="e.g. Smith" required />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium" style={{ color: theme.grey }}>Email {visitorType === 'volunteer' ? '(Required)' : '(Optional)'}</label>
                        <input type="email" id="email" name="apc-email" autoComplete="off" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={email} onChange={(e) => setEmail(e.target.value)} required={visitorType === 'volunteer'} />
                    </div>
                    <div>
                        <label htmlFor="visitor-type" className="block text-sm font-medium" style={{ color: theme.grey }}>Type of Visitor</label>
                        <select id="visitor-type" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={visitorType} onChange={(e) => { setVisitorType(e.target.value); setCheckType('') }} required>
                            <option value="">Select Type</option>
                            <option value="contractor">Contractor</option>
                            <option value="visitor">Visitor</option>
                            <option value="parentMeeting">Parent Meeting</option>
                            <option value="volunteer">Volunteer</option>
                            <option value="crt">CRT (Casual Relief Teacher)</option>
                            {/* Tutor is only offered at the Pickles St campus */}
                            {campus === '62 Pickles St' && <option value="tutor">Tutor</option>}
                        </select>
                    </div>

                    {visitorType === 'contractor' && (
                        <div>
                            <label htmlFor="company" className="block text-sm font-medium" style={{ color: theme.grey }}>Company Name</label>
                            <input type="text" id="company" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={company} onChange={(e) => setCompany(e.target.value)} required />
                        </div>
                    )}

                    {['contractor', 'visitor', 'volunteer', 'parentMeeting', 'tutor'].includes(visitorType) && (
                        <div>
                            <label htmlFor="purpose-of-visit" className="block text-sm font-medium" style={{ color: theme.grey }}>{visitorType === 'parentMeeting' ? 'Who are you meeting with?' : 'Purpose of Visit'}</label>
                            <textarea id="purpose-of-visit" rows="3" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" value={purposeOfVisit} onChange={(e) => setPurposeOfVisit(e.target.value)} required></textarea>
                        </div>
                    )}

                    {visitorType === 'crt' && (
                        <div>
                            <label htmlFor="vit-number" className="block text-sm font-medium" style={{ color: theme.grey }}>VIT Number</label>
                            <input type="text" id="vit-number" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" placeholder="e.g. 123456" maxLength={6} value={vitNumber} onChange={(e) => setVitNumber(e.target.value.replace(/\D/g, ''))} required />
                            {vitNumber && getVitValidationMsg(vitNumber) && (
                                <p className="mt-1 text-xs text-red-500">{getVitValidationMsg(vitNumber)}</p>
                            )}
                            <VitCheckStatusDisplay status={vitCheckStatus} result={vitCheckResult} lastName={lastName} vitNumber={vitNumber} theme={theme} />
                        </div>
                    )}

                    {['contractor', 'visitor', 'volunteer'].includes(visitorType) && (
                        <div className="p-3 border rounded-md" style={{ borderColor: theme.mediumGrey }}>
                            <label className="block text-sm font-medium" style={{ color: theme.grey }}>Do you have a Working With Children Check or are you VIT registered?</label>
                            <div className="mt-2 flex items-center space-x-6">
                                <label className="flex items-center">
                                    <input type="radio" name="checkType" value="wwcc" checked={checkType === 'wwcc'} onChange={(e) => setCheckType(e.target.value)} className="h-4 w-4" />
                                    <span className="ml-2 text-sm">WWCC</span>
                                </label>
                                <label className="flex items-center">
                                    <input type="radio" name="checkType" value="vit" checked={checkType === 'vit'} onChange={(e) => setCheckType(e.target.value)} className="h-4 w-4" />
                                    <span className="ml-2 text-sm">VIT</span>
                                </label>
                            </div>

                            {checkType === 'wwcc' && (
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label htmlFor="wwcc-number" className="block text-sm font-medium" style={{ color: theme.grey }}>WWCC Number</label>
                                        <input type="text" id="wwcc-number" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" placeholder="e.g. 1234567A-01" value={wwccNumber} onChange={(e) => setWwccNumber(e.target.value.toUpperCase())} required />
                                    </div>
                                    {!lastName.trim() && wwccNumber && (
                                        <p className="text-xs text-amber-600">Enter your last name above so we can verify your WWCC.</p>
                                    )}
                                    {wwccCheckStatus === 'checking' && (
                                        <p className="text-sm" style={{ color: theme.grey }}>⏳ Verifying...</p>
                                    )}
                                    {wwccCheckStatus === 'success' && wwccCheckResult && (
                                        <div className="p-3 rounded-md bg-green-50 border border-green-200 text-sm">
                                            <p className="font-semibold text-green-800">✓ WWCC is current</p>
                                            {wwccCheckResult.fullName && <p className="text-green-700">Card holder: {wwccCheckResult.fullName}</p>}
                                            {wwccCheckResult.expiryDate && <p className="text-green-700">Expires: {wwccCheckResult.expiryDate}</p>}
                                        </div>
                                    )}
                                    {wwccCheckStatus === 'failed' && wwccCheckResult && (
                                        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm">
                                            <p className="font-semibold text-red-800">✗ WWCC could not be verified</p>
                                            <p className="text-red-700">
                                                {wwccCheckResult.status === 'NO_MATCH' && 'The WWCC number and last name do not match. Please check and try again.'}
                                                {wwccCheckResult.status === 'REVOKED' && 'This WWCC has been revoked.'}
                                                {wwccCheckResult.status === 'SUSPENDED' && 'This WWCC is suspended.'}
                                                {wwccCheckResult.status === 'SURRENDERED' && 'This WWCC has been surrendered.'}
                                                {wwccCheckResult.isExpired && 'This WWCC has expired.'}
                                                {!['NO_MATCH', 'REVOKED', 'SUSPENDED', 'SURRENDERED'].includes(wwccCheckResult.status) && !wwccCheckResult.isExpired && 'Please see reception.'}
                                            </p>
                                        </div>
                                    )}
                                    {wwccCheckStatus === 'error' && (
                                        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
                                            <p className="font-semibold text-amber-800">⚠ WWCC service unreachable</p>
                                            <p className="text-amber-700">Sign-in is allowed but your WWCC will be flagged for manual review by reception.</p>
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500">As a condition of entry to Albert Park College, you consent to your Working With Children Check status being verified.</p>
                                </div>
                            )}

                            {checkType === 'vit' && (
                                <div className="mt-4">
                                    <label htmlFor="vit-number-visitor" className="block text-sm font-medium" style={{ color: theme.grey }}>VIT Number</label>
                                    <input type="text" id="vit-number-visitor" className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm" placeholder="e.g. 123456" maxLength={6} value={vitNumber} onChange={(e) => setVitNumber(e.target.value.replace(/\D/g, ''))} required />
                                    {vitNumber && getVitValidationMsg(vitNumber) && (
                                        <p className="mt-1 text-xs text-red-500">{getVitValidationMsg(vitNumber)}</p>
                                    )}
                                    <VitCheckStatusDisplay status={vitCheckStatus} result={vitCheckResult} lastName={lastName} vitNumber={vitNumber} theme={theme} />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex space-x-3">
                        <button type="button" onClick={() => { setMessage(''); setStep(1); }} className="px-4 py-3 font-semibold rounded-lg shadow-md" style={{ backgroundColor: 'transparent', color: theme.grey, border: `1px solid ${theme.mediumGrey}` }}>Back</button>
                        <button type="submit" className="flex-1 px-4 py-3 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: theme.blueDark }} disabled={!visitorType || isSubmitting || (checkType === 'wwcc' && wwccCheckStatus === 'checking') || vitCheckStatus === 'checking'}>{isSubmitting ? 'Signing In...' : (checkType === 'wwcc' && wwccCheckStatus === 'checking' ? 'Verifying WWCC...' : (vitCheckStatus === 'checking' ? 'Verifying VIT...' : 'Continue'))}</button>
                    </div>
                </form>
            )}

            {step === 3 && (
                <div className="space-y-4 text-center">
                    <h3 className="text-xl font-semibold capitalize" style={{ color: theme.grey }}>{visitorType} Sign In</h3>
                    <p className="text-sm" style={{ color: theme.grey }}>All visitors must adhere to relevant school policies. To receive a copy via email, please tick the box below.</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto p-2 border rounded text-left">
                        {(visitorType === 'volunteer' ? volunteerPolicies : contractorPolicies).map(p => <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">{p.name}</a>)}
                    </div>
                    <div className="flex items-start text-left">
                        <input id="policies-accepted" type="checkbox" className="h-4 w-4 mt-1" checked={policiesAccepted} onChange={(e) => setPoliciesAccepted(e.target.checked)} />
                        <label htmlFor="policies-accepted" className="ml-3 text-sm" style={{ color: theme.grey }}>I acknowledge I have been provided with relevant school policies and will adhere to them while onsite.</label>
                    </div>
                    {visitorType === 'volunteer' ? (
                        <p className="text-sm text-left p-2 rounded-md" style={{ color: theme.grey, backgroundColor: theme.mediumGrey }}>
                            A copy of these policies will be emailed to <span className="font-semibold">{email}</span>.
                        </p>
                    ) : (
                        <div className="flex items-start text-left">
                            <input id="email-policies" type="checkbox" className="h-4 w-4 mt-1" checked={sendPoliciesEmail} onChange={(e) => setSendPoliciesEmail(e.target.checked)} disabled={!email} />
                            <label htmlFor="email-policies" className={`ml-3 text-sm ${!email ? 'text-gray-400' : ''}`} style={{ color: theme.grey }}>Email me a copy of these policies {!email && '(Email required)'}</label>
                        </div>
                    )}
                    <div className="flex space-x-3">
                        <button type="button" onClick={() => { setMessage(''); setStep(2); }} className="px-4 py-3 font-semibold rounded-lg shadow-md" style={{ backgroundColor: 'transparent', color: theme.grey, border: `1px solid ${theme.mediumGrey}` }}>Back</button>
                        <button onClick={handleFinalSubmit} className="flex-1 px-4 py-3 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: theme.blueDark }} disabled={!policiesAccepted || isSubmitting}>{isSubmitting ? 'Signing In...' : 'Sign In'}</button>
                    </div>
                </div>
            )}

            {/* Reception recovery: if a visitor leaves the kiosk part-way
                through, staff can tap this to return it to a clean homepage
                without waiting for the idle reset. Only shown mid sign-in. */}
            {step !== 1 && !showConfirmationModal && (
                <div className="mt-6 pt-3 border-t text-center" style={{ borderColor: theme.mediumGrey }}>
                    <button type="button" onClick={resetForm} className="text-xs font-semibold opacity-40 hover:opacity-90 transition-opacity" style={{ color: theme.grey }}>↺ Start Over</button>
                </div>
            )}

            {showIdleWarning && !showConfirmationModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="rounded-2xl shadow-xl p-6 max-w-sm w-full text-center" style={{ backgroundColor: 'white' }}>
                        <h3 className="text-xl font-bold mb-3" style={{ color: theme.grey }}>Are you still there?</h3>
                        <p className="mb-4 text-sm" style={{ color: theme.grey }}>This form will reset shortly so the next visitor can sign in.</p>
                        <button onClick={bumpIdle} className="px-6 py-3 text-white font-semibold rounded-lg shadow-md" style={{ backgroundColor: theme.blueDark }}>I'm still here</button>
                    </div>
                </div>
            )}

            {showConfirmationModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
                    <div className="rounded-2xl shadow-xl p-6 max-w-sm w-full text-center" style={{ backgroundColor: 'white' }}>
                        <h3 className="text-xl font-bold mb-4" style={{ color: theme.grey }}>Sign-In Confirmed!</h3>
                        <p className="mb-4" style={{ color: theme.grey }}>You have successfully signed in. A confirmation message has been sent to your mobile.</p>
                        {['visitor', 'volunteer', 'parentMeeting', 'tutor'].includes(confirmedVisitorType) && (
                            <p className="mb-6 font-semibold p-3 rounded-md" style={{ color: theme.grey, backgroundColor: theme.mediumGrey }}>Please collect a visitor's lanyard from reception.</p>
                        )}
                        <button onClick={closeModal} className="px-6 py-3 text-white font-semibold rounded-lg shadow-md" style={{ backgroundColor: theme.blueDark }}>Close</button>
                        <p className="mt-3 text-xs" style={{ color: theme.grey }}>This window will close automatically in {Math.max(modalCountdown, 0)}s.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminPortal = ({ onLogout }) => {
    const { auth, db, theme, functions, functionsAu } = useContext(AppContext);
    const [signedInVisitors, setSignedInVisitors] = useState([]);
    const [message, setMessage] = useState('');
    const [campusFilter, setCampusFilter] = useState('All');
    const [showManualSignIn, setShowManualSignIn] = useState(false);
    const [editingVisitor, setEditingVisitor] = useState(null);
    const [newCampus, setNewCampus] = useState('');
    const [viewMode, setViewMode] = useState('live');
    const [historyDate, setHistoryDate] = useState(melbourneToday());
    const [historyVisitors, setHistoryVisitors] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportStartDate, setExportStartDate] = useState(melbourneToday());
    const [exportEndDate, setExportEndDate] = useState(melbourneToday());
    const [exportType, setExportType] = useState('full'); // 'full' | 'wwcc'
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [wwccRecords, setWwccRecords] = useState([]);
    const [isWwccLoading, setIsWwccLoading] = useState(false);
    const [bulkRecheckProgress, setBulkRecheckProgress] = useState(null); // null | { current, total }
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [markValidCard, setMarkValidCard] = useState(null);
    const [markValidExpiry, setMarkValidExpiry] = useState('');
    const [editingWwccCard, setEditingWwccCard] = useState(null);
    const [editWwccNumber, setEditWwccNumber] = useState('');
    const [wwccSortField, setWwccSortField] = useState(null);
    const [wwccSortDir, setWwccSortDir] = useState('asc');
    const [wwccFilter, setWwccFilter] = useState('');
    const [visitorHistoryModal, setVisitorHistoryModal] = useState(null);
    const [visitorHistoryEntries, setVisitorHistoryEntries] = useState([]);
    const [isVisitorHistoryLoading, setIsVisitorHistoryLoading] = useState(false);
    const [wwccStatusMap, setWwccStatusMap] = useState({});
    // Per-visitor live recheck results from the live page Re-check button: id -> { status, expiryDate, checking }
    const [liveWwccRechecks, setLiveWwccRechecks] = useState({});
    // Per-visitor live VIT register checks: id -> { checking, success, found, fullName, registrationType, expiryDate, isExpired, surnameMatches }
    const [liveVitChecks, setLiveVitChecks] = useState({});

    const allCampuses = ["All", "83 Danks St", "40 Bay St", "120 Bay St", "62 Pickles St"];
    const today = new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Australia/Melbourne' });

    // Admin custom claims go stale after 24h (requireAdmin in functions/index.js),
    // so a portal tab left open overnight gets permission-denied on every call.
    // Rather than surfacing that (it used to masquerade as "API Down" on live
    // checks), re-stamp the claim via checkAdminStatus, force-refresh the ID
    // token, and retry the failed call once. Concurrent failures share one
    // refresh via the ref.
    const adminClaimRefresh = useRef(null);
    const adminCallable = (name, instance = functions) => async (data) => {
        const fn = httpsCallable(instance, name);
        try {
            return await fn(data);
        } catch (err) {
            const user = auth?.currentUser;
            if (err.code !== 'functions/permission-denied' || !user?.email) throw err;
            if (!adminClaimRefresh.current) {
                adminClaimRefresh.current = (async () => {
                    try {
                        const recheck = await httpsCallable(functions, 'checkAdminStatus')({ email: user.email });
                        if (!recheck.data?.isAdmin) return false;
                        await user.getIdToken(true);
                        return true;
                    } catch (refreshErr) {
                        console.error('Admin claim refresh failed:', refreshErr);
                        return false;
                    } finally {
                        adminClaimRefresh.current = null;
                    }
                })();
            }
            if (!(await adminClaimRefresh.current)) throw err;
            return await fn(data);
        }
    };

    // Fetch WWCC check statuses once on load for live view badge display
    useEffect(() => {
        if (!functions) return;
        const fetchWwccStatusMap = async () => {
            try {
                const getWwccRecords = adminCallable('getWwccRecords');
                const result = await getWwccRecords({});
                if (result.data.success) {
                    const map = {};
                    result.data.records.forEach(r => { map[r.cardNumber] = { status: r.status, expiryDate: r.expiryDate || null, lastChecked: r.lastChecked || null }; });
                    setWwccStatusMap(map);
                }
            } catch (err) {
                console.error("Error fetching WWCC status map:", err);
            }
        };
        fetchWwccStatusMap();
    }, [functions]);

    // Live signed-in visitors via a Firestore real-time listener (replaces the
    // old 5-second polling of getSignedInVisitors — instant updates, far fewer reads).
    // Campus filtering is applied client-side so no composite index is required.
    useEffect(() => {
        if (viewMode !== 'live' || !db) return;

        const q = query(collection(db, 'visitors'), where('status', '==', 'signedIn'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const visitors = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                // Most recent sign-in first (Firestore Timestamps expose toMillis()).
                .sort((a, b) => (b.signInTime?.toMillis?.() || 0) - (a.signInTime?.toMillis?.() || 0));
            setSignedInVisitors(visitors);
        }, (error) => {
            console.error("Live visitors listener error:", error);
            setMessage('Error loading live visitors.');
        });

        return () => unsubscribe();
    }, [db, viewMode]);

    const fetchHistory = async (date) => {
        if (!functions) return;
        setIsHistoryLoading(true);
        setHistoryVisitors([]);
        setMessage('');

        try {
            const getVisitorHistory = adminCallable('getVisitorHistory');
            const result = await getVisitorHistory({
                startDate: date,
                endDate: date,
                campusFilter: 'All Campuses'
            });

            if (result.data.success) {
                // Convert ISO strings back to Date-like objects for display compatibility
                const visitors = result.data.visitors.map(v => ({
                    ...v,
                    signInTime: v.signInTime ? { toDate: () => new Date(v.signInTime) } : null,
                    signOutTime: v.signOutTime ? { toDate: () => new Date(v.signOutTime) } : null,
                }));
                setHistoryVisitors(visitors);
                if (visitors.length === 0) {
                    setMessage(`No records found for ${new Date(date).toLocaleDateString('en-AU')}.`);
                }
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error fetching history. Please try again.');
            }
        } finally {
            setIsHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (viewMode === 'history') {
            fetchHistory(historyDate);
        }
    }, [historyDate, viewMode]);

    const handleHistoryDownload = () => {
        if (historyVisitors.length === 0) {
            setMessage('No records to download for this date.');
            return;
        }

        const csvEscape = (val) => {
            const s = (val === undefined || val === null) ? '' : String(val);
            return `"${s.replace(/"/g, '""')}"`;
        };

        const timeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
        const headers = ['Name', 'Mobile', 'Email', 'Campus', 'Visitor Type', 'Company', 'Person Visiting', 'Purpose of Visit', 'WWCC Number', 'WWCC Expiry', 'VIT Number', 'Sign-In Time', 'Sign-Out Time', 'Status', 'Auto Sign-Out'];

        const rows = historyVisitors.map(v => {
            const signInDateObj = v.signInTime ? v.signInTime.toDate() : null;
            const signOutDateObj = v.signOutTime ? v.signOutTime.toDate() : null;
            return [
                v.name || '',
                v.mobileNumber || '',
                v.email || '',
                v.campus || '',
                v.visitorType || '',
                v.company || '',
                v.personVisiting || '',
                v.purposeOfVisit || '',
                v.wwccNumber || '',
                v.wwccExpiry || '',
                v.vitNumber || '',
                signInDateObj ? signInDateObj.toLocaleTimeString('en-AU', timeFormatOptions) : '',
                signOutDateObj ? signOutDateObj.toLocaleTimeString('en-AU', timeFormatOptions) : '',
                formatStatus(v.status),
                v.autoSignOut ? 'Yes' : 'No'
            ].map(csvEscape).join(',');
        });

        const csvContent = [headers.map(csvEscape).join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `visitor_history_${historyDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setMessage('History download generated successfully.');
    };

    const handleExport = async () => {
        if (!functions) { setMessage('Functions not connected.'); return; }
        setMessage('Generating export...');

        try {
            const getVisitorHistory = adminCallable('getVisitorHistory');
            const result = await getVisitorHistory({
                startDate: exportStartDate,
                endDate: exportEndDate,
                campusFilter: 'All Campuses'
            });

            if (!result.data.success || result.data.visitors.length === 0) {
                setMessage('No records found in the selected date range.');
                return;
            }

            // Filter for visitors with WWCC numbers
            const records = result.data.visitors.filter(v =>
                ['contractor', 'volunteer', 'visitor'].includes(v.visitorType) && v.wwccNumber
            );

            if (records.length === 0) {
                setMessage('No records with WWCC numbers found in the selected date range.');
                return;
            }

            let csvContent = '"Family Name","Card Number"\n';
            records.forEach(rec => {
                const nameParts = rec.name ? rec.name.split(' ') : ['Unknown'];
                const familyName = nameParts.length > 1 ? nameParts.pop() : rec.name;
                const cardNumber = rec.wwccNumber || '';

                if (cardNumber) { csvContent += `"${familyName}","${cardNumber}"\n`; }
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `wwcc_export_${exportStartDate}_to_${exportEndDate}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setMessage('Export generated successfully.');
            setShowExportModal(false);

        } catch (error) {
            console.error("Error exporting WWCC data:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error generating export. Please try again.');
            }
        }
    };

    // Full history export: every visit in the range with all details, one CSV.
    // Fetched month-by-month so a multi-year range stays under the callable
    // response size limit. WWCC validity is stamped from the check records
    // fetched at export time, not from the status at sign-in.
    const handleFullExport = async () => {
        if (!functions) { setMessage('Functions not connected.'); return; }
        if (exportStartDate > exportEndDate) {
            setExportProgress('Start date must be on or before end date.');
            return;
        }
        setIsExporting(true);
        try {
            setExportProgress('Fetching current WWCC check statuses...');
            const getWwccRecords = adminCallable('getWwccRecords');
            const wwccResult = await getWwccRecords({});
            const checkMap = {};
            if (wwccResult.data.success) {
                wwccResult.data.records.forEach(r => { checkMap[r.cardNumber] = r; });
            }

            const chunks = [];
            let cursor = new Date(`${exportStartDate}T00:00:00Z`);
            const rangeEnd = new Date(`${exportEndDate}T00:00:00Z`);
            while (cursor <= rangeEnd) {
                const monthEnd = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));
                const chunkEnd = monthEnd < rangeEnd ? monthEnd : rangeEnd;
                chunks.push({ start: cursor.toISOString().slice(0, 10), end: chunkEnd.toISOString().slice(0, 10) });
                cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
            }

            const getVisitorHistory = adminCallable('getVisitorHistory');
            const allVisitors = [];
            for (let i = 0; i < chunks.length; i++) {
                setExportProgress(`Fetching records... (month ${i + 1} of ${chunks.length})`);
                const result = await getVisitorHistory({
                    startDate: chunks[i].start,
                    endDate: chunks[i].end,
                    campusFilter: 'All Campuses'
                });
                if (result.data.success) allVisitors.push(...result.data.visitors);
            }

            if (allVisitors.length === 0) {
                setExportProgress('No records found in the selected date range.');
                return;
            }

            // signInTime arrives as an ISO string, so string compare sorts chronologically.
            allVisitors.sort((a, b) => (a.signInTime || '').localeCompare(b.signInTime || ''));

            const normalizeCard = (num) => (num || '').toUpperCase().replace(/\s/g, '').replace(/\//g, '-');
            const invalidLabels = { expired: 'Expired', revoked: 'Revoked', suspended: 'Suspended', surrendered: 'Surrendered', invalid: 'Invalid', no_match: 'No Match' };
            const wwccValidity = (v) => {
                if (!v.wwccNumber) return '';
                const status = checkMap[normalizeCard(v.wwccNumber)]?.status;
                if (status === 'current' || status === 'valid') return 'Yes';
                if (invalidLabels[status]) return `No (${invalidLabels[status]})`;
                return 'Not Checked';
            };

            const melb = { timeZone: 'Australia/Melbourne' };
            const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', melb) : '';
            const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true, ...melb }) : '';
            const csvEscape = (val) => {
                const s = (val === undefined || val === null) ? '' : String(val);
                return `"${s.replace(/"/g, '""')}"`;
            };

            const headers = ['Date', 'Name', 'Mobile', 'Email', 'Campus', 'Visitor Type', 'Company', 'Person Visiting', 'Purpose of Visit', 'WWCC Number', 'WWCC Expiry', 'WWCC Valid (at export date)', 'VIT Number', 'Sign-In Time', 'Sign-Out Time', 'Status', 'Auto Sign-Out'];
            const rows = allVisitors.map(v => [
                fmtDate(v.signInTime),
                v.name || '',
                v.mobileNumber || '',
                v.email || '',
                v.campus || '',
                formatVisitorType(v.visitorType),
                v.company || '',
                v.personVisiting || '',
                v.purposeOfVisit || '',
                v.wwccNumber || '',
                v.wwccExpiry || '',
                wwccValidity(v),
                v.vitNumber || '',
                fmtTime(v.signInTime),
                fmtTime(v.signOutTime),
                formatStatus(v.status),
                v.autoSignOut ? 'Yes' : 'No'
            ].map(csvEscape).join(','));

            const csvContent = [headers.map(csvEscape).join(','), ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `visitor_full_history_${exportStartDate}_to_${exportEndDate}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setExportProgress('');
            setShowExportModal(false);
            setMessage(`Full history export generated (${allVisitors.length} records).`);
        } catch (error) {
            console.error("Error exporting full history:", error);
            if (error.code === 'functions/permission-denied') {
                setExportProgress('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setExportProgress('Error generating export. Please try again.');
            }
        } finally {
            setIsExporting(false);
        }
    };

    const handleSignOut = async (visitor) => {
        if (!functions) return;
        if (!window.confirm(`Sign out ${visitor.name}?`)) return;
        try {
            const adminSignOutVisitor = adminCallable('adminSignOutVisitor');
            await adminSignOutVisitor({ visitorId: visitor.id });
            setMessage('Visitor signed out successfully!');
            // Live listener refreshes the list automatically.
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error signing out visitor:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error signing out visitor.');
            }
        }
    };

    const openChangeCampusModal = (visitor) => {
        setEditingVisitor(visitor);
        setNewCampus(visitor.campus);
    };

    const handleChangeCampus = async () => {
        if (!functions || !editingVisitor || !newCampus) return;
        try {
            const updateVisitorCampus = adminCallable('updateVisitorCampus');
            await updateVisitorCampus({ visitorId: editingVisitor.id, newCampus });
            setMessage('Campus updated successfully!');
            setEditingVisitor(null);
            // Live listener refreshes the list automatically.
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error updating campus:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error updating campus.');
            }
        }
    };

    const handleSignInAgain = async (visitor) => {
        if (!functions) return;

        try {
            // vitCheck is per-visit — a new sign-in needs a fresh check
            const { id, status, signInTime, signOutTime, vitCheck, ...oldData } = visitor;
            const visitorData = {
                ...oldData,
                signOutTime: null,
            };

            const adminManualSignIn = adminCallable('adminManualSignIn');
            const result = await adminManualSignIn({ visitorData });

            if (result.data.alreadySignedIn) {
                setMessage(`${visitor.name} is already signed in at ${visitor.campus}.`);
                return;
            }

            const visitorId = result.data.visitorId;

            // Send SMS notification (log failures rather than dropping silently)
            adminCallable('sendSignInSms')({ mobileNumber: visitor.mobileNumber, campus: visitor.campus, visitorId })
                .catch(err => console.error('sendSignInSms failed:', err));

            setMessage(`${visitor.name} has been signed back in successfully.`);
            setTimeout(() => setMessage(''), 5000);

        } catch (error) {
            console.error("Error signing visitor back in:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage("An error occurred while signing the visitor back in.");
            }
        }
    };

    const handleSearch = async () => {
        if (!functions || !searchQuery.trim()) {
            setMessage('Please enter a search term.');
            return;
        }

        setIsSearching(true);
        setSearchResults([]);
        setMessage('');

        try {
            const searchVisitors = adminCallable('searchVisitors');

            // First try searching by mobile number (normalized so "0412 345 678",
            // "+61412345678" and "0412345678" all match stored records)
            let result = await searchVisitors({
                searchTerm: normalizeMobile(searchQuery.trim()),
                searchType: 'mobile'
            });

            let visitors = result.data.visitors || [];

            // If no results by mobile, search by name
            if (visitors.length === 0) {
                result = await searchVisitors({
                    searchTerm: searchQuery.trim(),
                    searchType: 'name'
                });
                visitors = result.data.visitors || [];
            }

            // Convert ISO strings back to Date-like objects for display compatibility
            const formattedVisitors = visitors.map(v => ({
                ...v,
                signInTime: v.signInTime ? { toDate: () => new Date(v.signInTime) } : null,
                signOutTime: v.signOutTime ? { toDate: () => new Date(v.signOutTime) } : null,
            }));

            setSearchResults(formattedVisitors);

            if (formattedVisitors.length === 0) {
                setMessage(`No visitors found matching "${searchQuery}"`);
            }
        } catch (error) {
            console.error("Error searching visitors:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error searching visitors. Please try again.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    // WWCC Records functions
    const fetchWwccRecords = async () => {
        if (!functions) return;
        setIsWwccLoading(true);
        setMessage('');
        try {
            const getWwccRecords = adminCallable('getWwccRecords');
            const result = await getWwccRecords({});
            if (result.data.success) {
                setWwccRecords(result.data.records);
            }
        } catch (error) {
            console.error("Error fetching WWCC records:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Access denied. Your admin session may have expired — please reload the page.');
            } else {
                setMessage('Error loading WWCC records.');
            }
        } finally {
            setIsWwccLoading(false);
        }
    };

    useEffect(() => {
        if (viewMode === 'wwcc') {
            fetchWwccRecords();
        }
    }, [viewMode]);

    const handleMarkValid = async (cardNumber, expiryDate) => {
        if (!functions || !cardNumber || !expiryDate) return;
        try {
            const updateWwccCheck = adminCallable('updateWwccCheck');
            await updateWwccCheck({ cardNumber, status: 'valid', expiryDate });
            setMessage('WWCC check marked as valid.');
            setMarkValidCard(null);
            setMarkValidExpiry('');
            fetchWwccRecords();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error marking WWCC valid:", error);
            setMessage('Error updating WWCC check.');
        }
    };

    const handleDeleteWwccRecord = async (cardNumber) => {
        if (!functions || !cardNumber) return;
        if (!window.confirm(`Delete WWCC record for ${cardNumber}? This cannot be undone.`)) return;
        try {
            const deleteWwccRecord = adminCallable('deleteWwccRecord');
            await deleteWwccRecord({ cardNumber });
            // Optimistically remove from local state to avoid full reload
            setWwccRecords(prev => prev.filter(r => r.cardNumber !== cardNumber));
            setMessage('WWCC record deleted.');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error deleting WWCC record:", error);
            if (error.code === 'functions/permission-denied') {
                setMessage('Only authorized administrators can delete WWCC records.');
            } else {
                setMessage('Error deleting WWCC record.');
            }
        }
    };

    const handleEditWwccRecord = async (oldCardNumber, newCardNumber) => {
        if (!functions || !oldCardNumber || !newCardNumber) return;
        try {
            const editWwccRecord = adminCallable('editWwccRecord');
            await editWwccRecord({ oldCardNumber, newCardNumber });
            setMessage('WWCC card number updated.');
            setEditingWwccCard(null);
            setEditWwccNumber('');
            fetchWwccRecords();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error editing WWCC record:", error);
            if (error.message?.includes('already exists')) {
                setMessage('A record with that card number already exists.');
            } else {
                setMessage('Error updating WWCC card number.');
            }
        }
    };

    // Re-check a single signed-in visitor's WWCC against the Vic Gov API from the live page
    const handleLiveRecheckWwcc = async (visitor) => {
        if (!functions || !visitor.wwccNumber) return;
        const surname = (visitor.lastName || (visitor.name || '').trim().split(/\s+/).pop() || '').trim();
        if (!surname) {
            setMessage('Cannot re-check: no surname on record.');
            setTimeout(() => setMessage(''), 5000);
            return;
        }
        const cardKey = (visitor.wwccNumber || '').toUpperCase().replace(/\s/g, '').replace(/\//g, '-');
        setLiveWwccRechecks(prev => ({ ...prev, [visitor.id]: { ...prev[visitor.id], status: 'checking', checking: true } }));
        try {
            const checkWwcc = adminCallable('checkWwcc', functionsAu);
            const result = await checkWwcc({ cardNumber: visitor.wwccNumber, surname, visitorId: visitor.id });
            const data = result.data || {};
            if (data.success) {
                const status = (data.status || 'unknown').toLowerCase();
                setLiveWwccRechecks(prev => ({ ...prev, [visitor.id]: { status, expiryDate: data.expiryDate || null, checking: false } }));
                setWwccStatusMap(prev => ({ ...prev, [cardKey]: { status, expiryDate: data.expiryDate || null, lastChecked: new Date().toISOString() } }));
            } else {
                setLiveWwccRechecks(prev => ({ ...prev, [visitor.id]: { status: 'unverified_api_down', checking: false } }));
            }
        } catch (err) {
            console.error('Live WWCC recheck failed:', err);
            setLiveWwccRechecks(prev => ({ ...prev, [visitor.id]: { status: 'unverified_api_down', checking: false } }));
        }
    };

    // Check a signed-in visitor's VIT number against the public VIT register
    const handleLiveCheckVit = async (visitor) => {
        if (!functions || !visitor.vitNumber) return;
        setLiveVitChecks(prev => ({ ...prev, [visitor.id]: { checking: true } }));
        try {
            const checkVit = adminCallable('checkVit');
            const surname = (visitor.lastName || (visitor.name || '').trim().split(/\s+/).pop() || '').trim();
            const result = await checkVit({ registrationNumber: visitor.vitNumber, surname, visitorId: visitor.id });
            setLiveVitChecks(prev => ({ ...prev, [visitor.id]: { ...(result.data || {}), checking: false } }));
        } catch (err) {
            console.error('VIT check failed:', err);
            setLiveVitChecks(prev => ({ ...prev, [visitor.id]: { success: false, checking: false } }));
        }
    };

    const handleBulkRecheck = async () => {
        if (!functions || wwccRecords.length === 0) return;
        if (!window.confirm(`Re-check all ${wwccRecords.length} WWCC records against the Vic Gov API? This will take ~${wwccRecords.length} seconds.`)) return;

        const checkWwcc = adminCallable('checkWwcc', functionsAu);
        setBulkRecheckProgress({ current: 0, total: wwccRecords.length });
        let succeeded = 0;
        let failed = 0;

        for (let i = 0; i < wwccRecords.length; i++) {
            const rec = wwccRecords[i];
            setBulkRecheckProgress({ current: i + 1, total: wwccRecords.length });

            // Best-effort surname: last word of stored visitor name
            const nameParts = (rec.visitorName || '').trim().split(/\s+/);
            const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : (nameParts[0] || '');
            if (!surname || !rec.cardNumber) {
                failed++;
                continue;
            }

            try {
                const result = await checkWwcc({ cardNumber: rec.cardNumber, surname });
                const data = result.data || {};
                if (data.success) {
                    succeeded++;
                    // Update the local row optimistically so the UI reflects the new status
                    setWwccRecords(prev => prev.map(r => r.cardNumber === rec.cardNumber ? {
                        ...r,
                        status: (data.status || 'unknown').toLowerCase(),
                        statusMessage: data.rawMessage || r.statusMessage,
                        expiryDate: data.expiryDate || r.expiryDate,
                        cardType: data.cardType || r.cardType,
                        visitorName: data.fullName || r.visitorName,
                        lastChecked: { toDate: () => new Date() },
                    } : r));
                } else {
                    failed++;
                }
            } catch (err) {
                console.error(`Bulk recheck failed for ${rec.cardNumber}:`, err);
                failed++;
            }

            // Throttle to ~1/sec to be polite to the Vic Gov API
            if (i < wwccRecords.length - 1) {
                await new Promise(r => setTimeout(r, 1000));
            }
        }

        setBulkRecheckProgress(null);
        setMessage(`Bulk re-check complete: ${succeeded} succeeded, ${failed} failed.`);
        setTimeout(() => setMessage(''), 8000);
    };

    const handleViewVisitorHistory = async (visitorName, mobileNumber) => {
        setVisitorHistoryModal({ name: visitorName, mobile: mobileNumber });
        setVisitorHistoryEntries([]);
        setIsVisitorHistoryLoading(true);
        try {
            const searchVisitors = adminCallable('searchVisitors');
            const result = await searchVisitors({ searchTerm: mobileNumber, searchType: 'mobile' });
            const entries = (result.data.visitors || []).map(v => ({
                ...v,
                signInTime: v.signInTime ? { toDate: () => new Date(v.signInTime) } : null,
                signOutTime: v.signOutTime ? { toDate: () => new Date(v.signOutTime) } : null,
            }));
            setVisitorHistoryEntries(entries);
        } catch (error) {
            console.error("Error fetching visitor history:", error);
        } finally {
            setIsVisitorHistoryLoading(false);
        }
    };

    const handleImportCsv = async () => {
        if (!functions || !importFile) return;
        setIsImporting(true);
        setMessage('');

        try {
            const text = await importFile.text();
            const lines = text.split('\n').map(l => l.trim()).filter(l => l);
            if (lines.length < 2) {
                setMessage('CSV file appears empty or has no data rows.');
                setIsImporting(false);
                return;
            }

            // Parse header to find column indices
            const header = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
            const cardIdx = header.findIndex(h => h.includes('cardnumber') || h.includes('card number') || h === 'card_number');
            const statusIdx = header.findIndex(h => h.includes('statuscheck') || h.includes('status check') || h.includes('statusmessage') || h.includes('status_check'));

            if (cardIdx === -1 || statusIdx === -1) {
                setMessage('CSV must contain CardNumber and StatusCheckMessage columns. This looks like the export file — please import the check results CSV from the WWCC website instead.');
                setIsImporting(false);
                return;
            }

            // Parse data rows
            const parsedRows = [];
            const seenCards = {};
            for (let i = 1; i < lines.length; i++) {
                // Simple CSV parsing (handles quoted fields)
                const cols = lines[i].match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g);
                if (!cols) continue;
                const cleanCols = cols.map(c => c.replace(/^"|"$/g, '').trim());
                const cardNumber = (cleanCols[cardIdx] || '').toUpperCase().replace(/\s/g, '');
                const statusMessage = cleanCols[statusIdx] || '';

                if (!cardNumber) continue;

                // Deduplicate: take first non-"do not match" result for each card
                if (seenCards[cardNumber]) {
                    if (!seenCards[cardNumber].statusMessage.toLowerCase().includes('do not match') ||
                        statusMessage.toLowerCase().includes('do not match')) {
                        continue;
                    }
                }
                seenCards[cardNumber] = { cardNumber, statusMessage };
            }

            const rows = Object.values(seenCards);
            if (rows.length === 0) {
                setMessage('No valid rows found in CSV.');
                setIsImporting(false);
                return;
            }

            const importWwccChecks = adminCallable('importWwccChecks');
            const result = await importWwccChecks({ rows });

            if (result.data.success) {
                setMessage(`Import complete: ${result.data.updated} updated, ${result.data.notFound} not found in records.`);
                setShowImportModal(false);
                setImportFile(null);
                fetchWwccRecords();
            }
        } catch (error) {
            console.error("Error importing WWCC checks:", error);
            setMessage('Error importing WWCC checks. Please try again.');
        } finally {
            setIsImporting(false);
        }
    };

    const getWwccStatusBadge = (status) => {
        const styles = {
            valid: { bg: '#D1FAE5', color: '#065F46', label: 'Valid' },
            current: { bg: '#D1FAE5', color: '#065F46', label: 'Verified' },
            expired: { bg: '#FEE2E2', color: '#991B1B', label: 'Expired' },
            processing: { bg: '#FEF3C7', color: '#92400E', label: 'Processing' },
            checking: { bg: '#FEF3C7', color: '#92400E', label: 'Checking...' },
            not_checked: { bg: '#E5E7EB', color: '#374151', label: 'Not Checked' },
            unknown: { bg: '#E5E7EB', color: '#374151', label: 'Not Checked' },
            no_match: { bg: '#FFEDD5', color: '#9A3412', label: 'No Match' },
            revoked: { bg: '#FEE2E2', color: '#991B1B', label: 'Revoked' },
            suspended: { bg: '#FEE2E2', color: '#991B1B', label: 'Suspended' },
            surrendered: { bg: '#FEE2E2', color: '#991B1B', label: 'Surrendered' },
            unverified_api_down: { bg: '#FEF3C7', color: '#92400E', label: 'API Down' },
            invalid: { bg: '#FEE2E2', color: '#991B1B', label: 'Invalid' },
            vit_registered: { bg: '#D1FAE5', color: '#065F46', label: 'VIT Registered' },
            vit_provisional: { bg: '#D1FAE5', color: '#065F46', label: 'VIT Provisional' },
            vit_not_found: { bg: '#FEE2E2', color: '#991B1B', label: 'Not on Register' },
            vit_expired: { bg: '#FEE2E2', color: '#991B1B', label: 'VIT Expired' },
            vit_other: { bg: '#FEE2E2', color: '#991B1B', label: 'Check Status' },
        };
        const s = styles[status] || styles.not_checked;
        return <span className="px-2 py-1 text-xs font-semibold rounded-full" style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>;
    };

    const toggleWwccSort = (field) => {
        if (wwccSortField === field) {
            setWwccSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setWwccSortField(field);
            setWwccSortDir('asc');
        }
    };

    // 'current' = live-API verified, 'valid' = manually marked valid; both sort to the top.
    // revoked/suspended/surrendered group with other invalid states near the bottom.
    const statusOrder = { current: 0, valid: 0, processing: 1, not_checked: 2, unknown: 2, no_match: 3, expired: 4, revoked: 5, suspended: 5, surrendered: 5, invalid: 5, unverified_api_down: 6 };

    const sortedWwccRecords = [...wwccRecords].sort((a, b) => {
        if (!wwccSortField) return 0;
        let cmp = 0;
        if (wwccSortField === 'status') {
            cmp = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        } else if (wwccSortField === 'expiryDate') {
            const da = a.expiryDate || '';
            const db = b.expiryDate || '';
            cmp = da.localeCompare(db);
        }
        return wwccSortDir === 'asc' ? cmp : -cmp;
    });

    const sortIndicator = (field) => {
        if (wwccSortField !== field) return ' \u2195';
        return wwccSortDir === 'asc' ? ' \u2191' : ' \u2193';
    };

    const wwccFilterLower = wwccFilter.trim().toLowerCase();
    const displayedWwccRecords = wwccFilterLower ? sortedWwccRecords.filter(r =>
        (r.visitorName || '').toLowerCase().includes(wwccFilterLower) ||
        (r.cardNumber || '').toLowerCase().includes(wwccFilterLower) ||
        (r.mobileNumber || '').includes(wwccFilterLower)
    ) : sortedWwccRecords;

    const currentUserEmail = auth?.currentUser?.email;

    const getCampusColor = (campus) => ({ '83 Danks St': '#D1F2EB', '40 Bay St': '#D6EAF8', '120 Bay St': '#FCF3CF', '62 Pickles St': '#E8DAEF' }[campus] || 'transparent');

    // Apply the campus filter client-side (the live listener fetches all signed-in visitors).
    const visibleSignedIn = campusFilter === 'All' ? signedInVisitors : signedInVisitors.filter(v => v.campus === campusFilter);

    return (
        <div className="p-6 rounded-2xl shadow-xl w-full max-w-7xl" style={{ backgroundColor: 'white' }}>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold flex items-center" style={{ color: theme.grey }}>
                    {viewMode === 'live' ? (
                        <>
                            Visitors Onsite
                            <span className="ml-2.5 px-3 py-0.5 text-base font-bold rounded-full text-white" style={{ backgroundColor: theme.blueDark }}>{visibleSignedIn.length}</span>
                            <span className="ml-3 text-base font-normal opacity-60">{today}</span>
                        </>
                    ) : viewMode === 'wwcc' ? (
                        <>
                            Visitor WWCC Records
                            <span className="ml-2.5 px-3 py-0.5 text-base font-bold rounded-full text-white" style={{ backgroundColor: theme.blueDark }}>{wwccRecords.length}</span>
                        </>
                    ) : 'Visitor History'}
                    {/* Subtle manual page reload (low-key so it doesn't draw visitor attention). */}
                    <button onClick={() => window.location.reload()} title="Reload page" aria-label="Reload page" className="ml-2 text-base opacity-25 hover:opacity-80 transition-opacity" style={{ color: theme.grey }}>&#x21bb;</button>
                </h2>
                <div className="flex items-center space-x-2">
                    <button onClick={() => setViewMode('wwcc')} className="px-4 py-1.5 text-sm font-semibold rounded-lg shadow-sm" style={{ backgroundColor: theme.yellow, color: theme.grey }}>Visitor WWCC Records</button>
                    <button onClick={() => setShowManualSignIn(true)} className="px-4 py-1.5 text-sm font-semibold rounded-lg shadow-sm" style={{ backgroundColor: theme.yellow, color: theme.grey }}>Manual Sign In</button>
                    <span className="h-6 border-l" style={{ borderColor: theme.mediumGrey }} aria-hidden="true" />
                    <button onClick={onLogout} className="px-2 py-1.5 text-sm font-semibold opacity-60 hover:opacity-100 hover:underline transition-opacity" style={{ color: theme.grey }}>Logout</button>
                </div>
            </div>

            <div className="mb-4 flex items-center space-x-2 border-b pb-4">
                <span className="font-semibold" style={{ color: theme.grey }}>View:</span>
                <button onClick={() => setViewMode('live')} className={`px-4 py-2 rounded-lg shadow-sm text-sm font-semibold ${viewMode === 'live' ? 'text-white' : ''}`} style={{ backgroundColor: viewMode === 'live' ? theme.blueDark : theme.mediumGrey, color: viewMode === 'live' ? 'white' : theme.grey }}>Live View</button>
                <button onClick={() => setViewMode('history')} className={`px-4 py-2 rounded-lg shadow-sm text-sm font-semibold ${viewMode === 'history' ? 'text-white' : ''}`} style={{ backgroundColor: viewMode === 'history' ? theme.blueDark : theme.mediumGrey, color: viewMode === 'history' ? 'white' : theme.grey }}>History View</button>
                <button onClick={() => setViewMode('search')} className={`px-4 py-2 rounded-lg shadow-sm text-sm font-semibold ${viewMode === 'search' ? 'text-white' : ''}`} style={{ backgroundColor: viewMode === 'search' ? theme.blueDark : theme.mediumGrey, color: viewMode === 'search' ? 'white' : theme.grey }}>Search</button>
                {viewMode === 'history' && <input type="date" value={historyDate} onChange={(e) => setHistoryDate(e.target.value)} className="ml-4 p-2 border rounded-md shadow-sm" />}
                {viewMode === 'history' && historyVisitors.length > 0 && !isHistoryLoading && (
                    <button
                        onClick={handleHistoryDownload}
                        className="px-4 py-2 rounded-lg shadow-sm text-sm font-semibold"
                        style={{ backgroundColor: theme.yellow, color: theme.grey }}
                    >
                        Download CSV
                    </button>
                )}
                {viewMode === 'history' && (
                    <button
                        onClick={() => { setExportType('full'); setExportProgress(''); setShowExportModal(true); }}
                        className="px-4 py-2 rounded-lg shadow-sm text-sm font-semibold"
                        style={{ backgroundColor: theme.yellow, color: theme.grey }}
                    >
                        Export Full Report
                    </button>
                )}
            </div>

            {message && <p className={`text-sm my-2 px-3 py-2 rounded-lg inline-block ${message.includes('Error') || message.includes('must') || message.includes('Please') || message.includes('already') || message.includes('denied') ? 'text-red-700 bg-red-50' : 'text-green-700 bg-green-50'}`}>{message}</p>}

            {viewMode === 'live' ? (
                <>
                    <div className="mb-4 flex items-center space-x-2">
                        <label className="font-semibold" style={{ color: theme.grey }}>Filter by Campus:</label>
                        {allCampuses.map(campus => {
                            const count = campus === 'All' ? signedInVisitors.length : signedInVisitors.filter(v => v.campus === campus).length;
                            return (
                                <button key={campus} onClick={() => setCampusFilter(campus)} className={`px-3 py-1 text-sm font-semibold rounded-full shadow-sm ${campusFilter === campus ? 'ring-2 ring-offset-1' : ''}`} style={{ backgroundColor: getCampusColor(campus), '--tw-ring-color': theme.blue }}>
                                    {campus}
                                    <span className={`ml-1.5 px-1.5 py-0.5 text-xs font-bold rounded-full ${count === 0 ? 'opacity-50' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}>{count}</span>
                                </button>
                            );
                        })}
                    </div>
                    {visibleSignedIn.length > 0 ? <LiveVisitorsTable visitors={visibleSignedIn} handleSignOut={handleSignOut} getCampusColor={getCampusColor} theme={theme} wwccStatusMap={wwccStatusMap} getWwccStatusBadge={getWwccStatusBadge} liveWwccRechecks={liveWwccRechecks} handleLiveRecheckWwcc={handleLiveRecheckWwcc} liveVitChecks={liveVitChecks} handleLiveCheckVit={handleLiveCheckVit} /> : <EmptyState icon="&#128075;" title={campusFilter === 'All' ? 'No visitors onsite right now' : `No visitors onsite at ${campusFilter}`} subtitle="New sign-ins appear here instantly." />}
                </>
            ) : viewMode === 'history' ? (
                isHistoryLoading ? <p>Loading history...</p> : historyVisitors.length > 0 ? <HistoryVisitorsTable visitors={historyVisitors} theme={theme} handleSignInAgain={handleSignInAgain} /> : <EmptyState icon="&#128197;" title={message || 'No records for this date'} subtitle="Pick another date above to browse past sign-ins." />
            ) : viewMode === 'search' ? (
                <>
                    <div className="mb-4 space-y-4">
                        <div className="flex items-center space-x-2">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="Search by name, mobile number, or email"
                                className="flex-1 p-2 border rounded-md shadow-sm"
                                style={{ borderColor: theme.mediumGrey }}
                            />
                            <button
                                onClick={handleSearch}
                                disabled={isSearching}
                                className="px-4 py-2 text-white font-semibold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: isSearching ? theme.mediumGrey : theme.blueDark }}
                            >
                                {isSearching ? 'Searching...' : 'Search'}
                            </button>
                        </div>
                        <p className="text-sm" style={{ color: theme.grey }}>
                            Search across all visitor records by name, mobile number, or email address.
                        </p>
                    </div>
                    {searchResults.length > 0 ? (
                        <SearchResultsTable visitors={searchResults} theme={theme} getCampusColor={getCampusColor} handleSignInAgain={handleSignInAgain} />
                    ) : (
                        !isSearching && searchQuery && <EmptyState icon="&#128269;" title={message || 'Press Search to look up visitors'} subtitle={message ? 'Try a partial name, a different spelling, or the mobile number.' : 'You can search by name, mobile number, or email.'} />
                    )}
                </>
            ) : viewMode === 'wwcc' ? (
                <>
                    <div className="mb-4 flex items-center space-x-2">
                        <button onClick={() => setViewMode('live')} className="px-4 py-2 rounded-lg shadow-sm text-sm font-semibold" style={{ backgroundColor: theme.mediumGrey, color: theme.grey }}>Back to Live View</button>
                        <button onClick={handleBulkRecheck} disabled={!!bulkRecheckProgress || wwccRecords.length === 0} className="px-4 py-2 rounded-lg shadow-sm text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: theme.blueDark, color: 'white' }}>
                            {bulkRecheckProgress ? `Checking ${bulkRecheckProgress.current} / ${bulkRecheckProgress.total}...` : 'Re-check All WWCCs'}
                        </button>
                        <input
                            type="text"
                            value={wwccFilter}
                            onChange={(e) => setWwccFilter(e.target.value)}
                            placeholder="Filter by name, card or mobile"
                            className="px-3 py-2 border rounded-md shadow-sm text-sm flex-1"
                            style={{ borderColor: theme.mediumGrey, maxWidth: '280px' }}
                        />
                    </div>
                    {isWwccLoading ? (
                        <p style={{ color: theme.grey }}>Loading WWCC records...</p>
                    ) : wwccRecords.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full border rounded-lg">
                                <thead className="bg-gray-100" style={{ color: theme.grey }}>
                                    <tr>
                                        {['Name', 'Mobile', 'WWCC Number'].map(h =>
                                            <th key={h} className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>
                                        )}
                                        <th className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleWwccSort('status')}>Status{sortIndicator('status')}</th>
                                        <th className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleWwccSort('expiryDate')}>Expiry Date{sortIndicator('expiryDate')}</th>
                                        {['Last Checked', 'Actions'].map(h =>
                                            <th key={h} className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>
                                        )}
                                        {['jacksperling@albertparkcollege.vic.edu.au', 'ericahess@albertparkcollege.vic.edu.au'].includes(currentUserEmail) && (
                                            <th className="py-3 px-2 border-b" style={{ width: '30px' }}></th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedWwccRecords.map(rec => (
                                        <tr key={rec.cardNumber} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                                <button
                                                    onClick={() => handleViewVisitorHistory(rec.visitorName, rec.mobileNumber)}
                                                    className="text-blue-600 hover:underline text-left"
                                                >{rec.visitorName}</button>
                                            </td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{rec.mobileNumber}</td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap font-mono">
                                                <div className="flex items-center">
                                                    <span className="inline-block" style={{ minWidth: '140px' }}>{rec.cardNumber}</span>
                                                    {editingWwccCard === rec.cardNumber ? (
                                                        <span className="inline-flex items-center space-x-1 ml-2">
                                                            <input
                                                                type="text"
                                                                value={editWwccNumber}
                                                                onChange={(e) => setEditWwccNumber(e.target.value)}
                                                                className="px-2 py-1 border rounded text-sm w-32"
                                                            />
                                                            <button
                                                                onClick={() => handleEditWwccRecord(rec.cardNumber, editWwccNumber)}
                                                                disabled={!editWwccNumber.trim()}
                                                                className="px-2 py-1 text-xs font-semibold rounded-md text-white"
                                                                style={{ backgroundColor: editWwccNumber.trim() ? theme.blueDark : theme.mediumGrey }}
                                                            >Save</button>
                                                            <button
                                                                onClick={() => { setEditingWwccCard(null); setEditWwccNumber(''); }}
                                                                className="px-2 py-1 text-xs font-semibold rounded-md"
                                                                style={{ backgroundColor: theme.mediumGrey }}
                                                            >Cancel</button>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => { setEditingWwccCard(rec.cardNumber); setEditWwccNumber(rec.cardNumber); }}
                                                            className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-md"
                                                            style={{ backgroundColor: 'transparent', color: theme.blueDark, border: `1px solid ${theme.blueDark}` }}
                                                        >Edit</button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{getWwccStatusBadge(rec.status)}</td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{rec.expiryDate ? rec.expiryDate.split('-').reverse().join('-') : '-'}</td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{rec.lastChecked ? new Date(rec.lastChecked).toISOString().split('T')[0].split('-').reverse().join('-') : '-'}</td>
                                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    {markValidCard === rec.cardNumber ? (
                                                        <>
                                                            <input
                                                                type="date"
                                                                value={markValidExpiry}
                                                                onChange={(e) => setMarkValidExpiry(e.target.value)}
                                                                className="px-2 py-1 border rounded text-sm"
                                                            />
                                                            <button
                                                                onClick={() => handleMarkValid(rec.cardNumber, markValidExpiry)}
                                                                disabled={!markValidExpiry}
                                                                className="px-2 py-1 text-xs font-semibold rounded-md text-white"
                                                                style={{ backgroundColor: markValidExpiry ? '#059669' : theme.mediumGrey }}
                                                            >Save</button>
                                                            <button
                                                                onClick={() => { setMarkValidCard(null); setMarkValidExpiry(''); }}
                                                                className="px-2 py-1 text-xs font-semibold rounded-md"
                                                                style={{ backgroundColor: theme.mediumGrey }}
                                                            >Cancel</button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {rec.status !== 'valid' && (
                                                                <button
                                                                    onClick={() => { setMarkValidCard(rec.cardNumber); setMarkValidExpiry(rec.expiryDate || ''); }}
                                                                    className="px-3 py-1 text-sm font-semibold rounded-md"
                                                                    style={{ backgroundColor: 'transparent', color: '#059669', border: '1px solid #059669' }}
                                                                >Mark Valid</button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            {['jacksperling@albertparkcollege.vic.edu.au', 'ericahess@albertparkcollege.vic.edu.au'].includes(currentUserEmail) && (
                                                <td className="py-3 px-2 text-sm text-center">
                                                    <button
                                                        onClick={() => handleDeleteWwccRecord(rec.cardNumber)}
                                                        className="text-gray-400 hover:text-red-600 font-bold text-lg leading-none"
                                                        title="Delete record"
                                                    >&times;</button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="mt-4" style={{ color: theme.grey }}>No WWCC records found.</p>
                    )}
                </>
            ) : null}

            {showManualSignIn && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50"><div className="relative p-8 bg-white w-full max-w-lg rounded-2xl shadow-xl"><button onClick={() => setShowManualSignIn(false)} className="absolute top-4 right-4 text-2xl font-bold">×</button><h3 className="text-xl font-semibold mb-4 text-center">Manual Visitor Sign In</h3><VisitorSignIn /></div></div>}
            {editingVisitor && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50"><div className="relative p-8 bg-white w-full max-w-sm rounded-2xl shadow-xl space-y-4"><h3 className="text-xl font-semibold text-center">Change Campus for {editingVisitor.name}</h3><div><label htmlFor="new-campus-select" className="block text-sm font-medium">New Campus</label><select id="new-campus-select" value={newCampus} onChange={(e) => setNewCampus(e.target.value)} className="mt-1 block w-full p-2 border rounded-md">{allCampuses.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}</select></div><div className="flex justify-end space-x-4 mt-4"><button onClick={() => setEditingVisitor(null)} className="px-4 py-2 rounded-lg shadow-md" style={{ backgroundColor: theme.mediumGrey }}>Cancel</button><button onClick={handleChangeCampus} className="px-4 py-2 text-white font-semibold rounded-lg shadow-md" style={{ backgroundColor: theme.blueDark }}>Save</button></div></div></div>}
            {showExportModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50"><div className="relative p-8 bg-white w-full max-w-md rounded-2xl shadow-xl space-y-4">
                <h3 className="text-xl font-semibold text-center">Export Report</h3>
                <p className="text-sm text-center">Select a report type and date range.</p>
                <div className="space-y-2">
                    <label className="flex items-start space-x-2 cursor-pointer">
                        <input type="radio" name="export-type" className="mt-1" checked={exportType === 'full'} onChange={() => setExportType('full')} disabled={isExporting} />
                        <span className="text-sm"><span className="font-medium">Full visitor history</span><br /><span style={{ color: theme.grey }}>All sign-in details including WWCC/VIT, for compliance reporting.</span></span>
                    </label>
                    <label className="flex items-start space-x-2 cursor-pointer">
                        <input type="radio" name="export-type" className="mt-1" checked={exportType === 'wwcc'} onChange={() => setExportType('wwcc')} disabled={isExporting} />
                        <span className="text-sm"><span className="font-medium">WWCC details only</span><br /><span style={{ color: theme.grey }}>Surname and card number, for the WWCC website bulk status check.</span></span>
                    </label>
                </div>
                <div><label htmlFor="start-date" className="block text-sm font-medium">Start Date</label><input type="date" id="start-date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} disabled={isExporting} className="mt-1 block w-full p-2 border rounded-md" /></div>
                <div><label htmlFor="end-date" className="block text-sm font-medium">End Date</label><input type="date" id="end-date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} disabled={isExporting} className="mt-1 block w-full p-2 border rounded-md" /></div>
                {exportProgress && <p className={`text-sm text-center px-3 py-2 rounded-lg ${exportProgress.includes('Error') || exportProgress.includes('denied') || exportProgress.includes('must') ? 'text-red-700 bg-red-50' : 'text-blue-700 bg-blue-50'}`}>{exportProgress}</p>}
                <div className="flex justify-end space-x-4 mt-4">
                    <button onClick={() => setShowExportModal(false)} disabled={isExporting} className="px-4 py-2 rounded-lg shadow-md disabled:opacity-50" style={{ backgroundColor: theme.mediumGrey }}>Cancel</button>
                    <button onClick={() => exportType === 'full' ? handleFullExport() : handleExport()} disabled={isExporting} className="px-4 py-2 text-white font-semibold rounded-lg shadow-md disabled:opacity-50" style={{ backgroundColor: theme.blueDark }}>{isExporting ? 'Generating...' : 'Generate & Download CSV'}</button>
                </div>
            </div></div>}
            {showImportModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
                    <div className="relative p-8 bg-white w-full max-w-md rounded-2xl shadow-xl space-y-4">
                        <h3 className="text-xl font-semibold text-center">Import WWCC Check Results</h3>
                        <p className="text-sm text-center" style={{ color: theme.grey }}>Upload the CSV file exported from the WWCC website status check.</p>
                        <div>
                            <label htmlFor="import-csv" className="block text-sm font-medium" style={{ color: theme.grey }}>CSV File</label>
                            <input
                                type="file"
                                id="import-csv"
                                accept=".csv"
                                onChange={(e) => setImportFile(e.target.files[0] || null)}
                                className="mt-1 block w-full p-2 border rounded-md text-sm"
                            />
                        </div>
                        <div className="flex justify-end space-x-4 mt-4">
                            <button onClick={() => { setShowImportModal(false); setImportFile(null); }} className="px-4 py-2 rounded-lg shadow-md" style={{ backgroundColor: theme.mediumGrey }}>Cancel</button>
                            <button
                                onClick={handleImportCsv}
                                disabled={!importFile || isImporting}
                                className="px-4 py-2 text-white font-semibold rounded-lg shadow-md"
                                style={{ backgroundColor: (!importFile || isImporting) ? theme.mediumGrey : theme.blueDark }}
                            >{isImporting ? 'Importing...' : 'Import'}</button>
                        </div>
                    </div>
                </div>
            )}
            {visitorHistoryModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
                    <div className="relative p-8 bg-white w-full max-w-4xl max-h-[80vh] rounded-2xl shadow-xl flex flex-col">
                        <button onClick={() => setVisitorHistoryModal(null)} className="absolute top-4 right-4 text-2xl font-bold text-gray-400 hover:text-gray-600">&times;</button>
                        <h3 className="text-xl font-semibold mb-1" style={{ color: theme.grey }}>{visitorHistoryModal.name}</h3>
                        <p className="text-sm mb-4" style={{ color: theme.grey }}>{visitorHistoryModal.mobile}</p>
                        {isVisitorHistoryLoading ? (
                            <p style={{ color: theme.grey }}>Loading history...</p>
                        ) : visitorHistoryEntries.length > 0 ? (
                            <div className="overflow-auto flex-1">
                                <table className="min-w-full border rounded-lg">
                                    <thead className="bg-gray-100" style={{ color: theme.grey }}>
                                        <tr>
                                            {['Date', 'Campus', 'Type', 'Purpose', 'Sign In', 'Sign Out', 'Status'].map(h =>
                                                <th key={h} className="py-2 px-3 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visitorHistoryEntries.map(v => {
                                            const signIn = v.signInTime ? v.signInTime.toDate() : null;
                                            const signOut = v.signOutTime ? v.signOutTime.toDate() : null;
                                            return (
                                                <tr key={v.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{signIn ? signIn.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}</td>
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{v.campus}</td>
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{formatVisitorType(v.visitorType)}</td>
                                                    <td className="py-2 px-3 text-sm max-w-xs truncate">{v.purposeOfVisit}</td>
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{signIn ? signIn.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}</td>
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{signOut ? <>{signOut.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}{v.autoSignOut && <span className="ml-1 text-xs italic text-gray-400">(Auto)</span>}</> : '-'}</td>
                                                    <td className="py-2 px-3 text-sm whitespace-nowrap">{formatStatus(v.status)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p style={{ color: theme.grey }}>No historical entries found.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const LiveVisitorsTable = ({ visitors, handleSignOut, getCampusColor, theme, wwccStatusMap, getWwccStatusBadge, liveWwccRechecks = {}, handleLiveRecheckWwcc, liveVitChecks = {}, handleLiveCheckVit }) => {
    const timeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    const normalizeCard = (num) => (num || '').toUpperCase().replace(/\s/g, '').replace(/\//g, '-');
    // Map a VIT register result to a badge status key
    const vitBadgeStatus = (vit) => {
        if (!vit.found) return 'vit_not_found';
        if (vit.isExpired) return 'vit_expired';
        const t = (vit.registrationType || '').toLowerCase();
        if (t.startsWith('provisionally')) return 'vit_provisional';
        if (t.includes('registered teacher')) return 'vit_registered';
        return 'vit_other';
    };
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border rounded-lg">
                <thead className="bg-gray-100" style={{ color: theme.grey }}>
                <tr>{['Name', 'Mobile', 'Campus', 'Type', 'Details', 'WWCC / VIT', 'Purpose', 'Sign-In Time', 'Actions'].map(h => <th key={h} className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>)}</tr>
                </thead>
                <tbody>
                {visitors.map(v => {
                    const cardKey = normalizeCard(v.wwccNumber);
                    const recheck = liveWwccRechecks[v.id];
                    // Prefer this session's check, then the result stamped on the
                    // visitor record (visible to all admins via the live listener)
                    const vit = liveVitChecks[v.id] || (v.vitCheck ? { ...v.vitCheck, success: true } : undefined);
                    const mapEntry = (cardKey && wwccStatusMap[cardKey]) || null;
                    // Prefer an explicit re-check this session, then the visitor's own sign-in
                    // verification, then the cached records map, then fall back to not checked.
                    // A sign-in flagged "API Down" defers to a check of the same card made
                    // later that day (e.g. a bulk re-check), so the flag clears on reload.
                    const melbDay = (d) => new Date(d).toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' });
                    const mapCheckedToday = mapEntry?.lastChecked && melbDay(mapEntry.lastChecked) === melbDay(Date.now());
                    const docStatus = (v.wwccStatus === 'unverified_api_down' && mapCheckedToday) ? mapEntry.status : v.wwccStatus;
                    const wwccStatus = recheck?.status || docStatus || mapEntry?.status || (v.wwccNumber ? 'not_checked' : null);
                    const wwccExpiry = recheck?.expiryDate || v.wwccExpiry || mapEntry?.expiryDate || null;
                    const isVerified = wwccStatus === 'current' || wwccStatus === 'valid';
                    return (
                        <tr key={v.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.name}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.mobileNumber}</td>
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap"><span className="px-2 py-1 text-xs font-semibold rounded-md" style={{ backgroundColor: getCampusColor(v.campus) }}>{v.campus}</span></td>
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{formatVisitorType(v.visitorType)}</td>
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.vitNumber ? `VIT: ${v.vitNumber}` : v.wwccNumber ? `WWCC: ${v.wwccNumber}` : ''}</td>
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                {wwccStatus ? (
                                    <div className="flex flex-col items-start space-y-1">
                                        {getWwccStatusBadge(wwccStatus)}
                                        {isVerified && wwccExpiry && (
                                            <span className="text-xs" style={{ color: theme.grey }}>Expires {wwccExpiry}</span>
                                        )}
                                        {v.wwccNumber && handleLiveRecheckWwcc && (
                                            <button
                                                onClick={() => handleLiveRecheckWwcc(v)}
                                                disabled={recheck?.checking}
                                                className="text-xs font-semibold hover:underline disabled:opacity-50"
                                                style={{ color: theme.blueDark }}
                                            >{recheck?.checking ? 'Checking...' : 'Re-check'}</button>
                                        )}
                                    </div>
                                ) : v.vitNumber ? (
                                    <div className="flex flex-col items-start space-y-1">
                                        {vit && !vit.checking && (
                                            vit.success === false ? getWwccStatusBadge('unverified_api_down') : getWwccStatusBadge(vitBadgeStatus(vit))
                                        )}
                                        {vit && !vit.checking && vit.found && (
                                            <span className="text-xs" style={{ color: vit.surnameMatches === false ? '#9A3412' : theme.grey }}>
                                                {vit.fullName}{vit.surnameMatches === false ? ' — name differs!' : ''}
                                            </span>
                                        )}
                                        {vit && !vit.checking && vit.found && vit.registrationType && vitBadgeStatus(vit) === 'vit_other' && (
                                            <span className="text-xs" style={{ color: '#9A3412' }}>{vit.registrationType}</span>
                                        )}
                                        {vit && !vit.checking && vit.found && vit.expiryDate && !vit.isExpired && (
                                            <span className="text-xs" style={{ color: theme.grey }}>Expires {vit.expiryDate}</span>
                                        )}
                                        {handleLiveCheckVit && (
                                            <button
                                                onClick={() => handleLiveCheckVit(v)}
                                                disabled={vit?.checking}
                                                className="text-xs font-semibold hover:underline disabled:opacity-50"
                                                style={{ color: theme.blueDark }}
                                            >{vit?.checking ? 'Checking...' : (vit ? 'Re-check VIT' : 'Check VIT')}</button>
                                        )}
                                    </div>
                                ) : '-'}
                            </td>
                            <td className="py-2.5 px-4 text-sm max-w-xs truncate">{v.purposeOfVisit}</td>
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.signInTime ? v.signInTime.toDate().toLocaleTimeString('en-AU', timeFormatOptions) : 'N/A'}</td>
                            <td className="py-3 px-4 whitespace-nowrap">
                                <button onClick={() => handleSignOut(v)} className="px-3 py-1 text-sm font-semibold rounded-md" style={{ backgroundColor: 'transparent', color: theme.blueDark, border: `1px solid ${theme.blueDark}` }}>Sign Out</button>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}

const formatStatus = (status) => {
    if (!status) return 'N/A';
    const result = status.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
};

const HistoryVisitorsTable = ({ visitors, theme, handleSignInAgain }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full border rounded-lg">
            <thead className="bg-gray-100" style={{ color: theme.grey }}>
            <tr>{['Name', 'Mobile', 'Campus', 'Type', 'Details', 'Sign-In Time', 'Sign-Out Time', 'Status', 'Actions'].map(h => <th key={h} className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
            {visitors.map(v => {
                const signInDateObj = v.signInTime ? v.signInTime.toDate() : null;
                const signOutDateObj = v.signOutTime ? v.signOutTime.toDate() : null;
                const timeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };

                return (
                    <tr key={v.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.name}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.mobileNumber}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.campus}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{formatVisitorType(v.visitorType)}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.vitNumber ? `VIT: ${v.vitNumber}` : v.wwccNumber ? `WWCC: ${v.wwccNumber}` : ''}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{signInDateObj ? signInDateObj.toLocaleTimeString('en-AU', timeFormatOptions) : 'N/A'}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{signOutDateObj ? <>{signOutDateObj.toLocaleTimeString('en-AU', timeFormatOptions)}{v.autoSignOut && <span className="ml-1 text-xs italic text-gray-400">(Auto)</span>}</> : 'Not Signed Out'}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">{formatStatus(v.status)}</td>
                        <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                            {v.status === 'signedOut' && (
                                <button
                                    onClick={() => handleSignInAgain(v)}
                                    className="px-3 py-1 text-sm font-semibold rounded-md"
                                    style={{ backgroundColor: 'transparent', color: theme.blueDark, border: `1px solid ${theme.blueDark}` }}
                                >
                                    Sign Back In
                                </button>
                            )}
                        </td>
                    </tr>
                )
            })}
            </tbody>
        </table>
    </div>
);

const SearchResultsTable = ({ visitors, theme, getCampusColor, handleSignInAgain }) => {
    const dateFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const timeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full border rounded-lg">
                <thead className="bg-gray-100" style={{ color: theme.grey }}>
                    <tr>
                        {['Name', 'Mobile', 'Email', 'Campus', 'Type', 'Details', 'Sign-In Date', 'Sign-In Time', 'Sign-Out Time', 'Status', 'Actions'].map(h =>
                            <th key={h} className="py-2.5 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider">{h}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {visitors.map(v => {
                        const signInDateObj = v.signInTime ? v.signInTime.toDate() : null;
                        const signOutDateObj = v.signOutTime ? v.signOutTime.toDate() : null;

                        return (
                            <tr key={v.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.name}</td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">{v.mobileNumber}</td>
                                <td className="py-2.5 px-4 text-sm truncate max-w-xs">{v.email || 'N/A'}</td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    <span className="px-2 py-1 text-xs font-semibold rounded-md"
                                          style={{ backgroundColor: getCampusColor(v.campus) }}>
                                        {v.campus}
                                    </span>
                                </td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">{formatVisitorType(v.visitorType)}</td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    {v.vitNumber ? `VIT: ${v.vitNumber}` : v.wwccNumber ? `WWCC: ${v.wwccNumber}` : ''}
                                </td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    {signInDateObj ? signInDateObj.toLocaleDateString('en-AU', dateFormatOptions) : 'N/A'}
                                </td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    {signInDateObj ? signInDateObj.toLocaleTimeString('en-AU', timeFormatOptions) : 'N/A'}
                                </td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    {signOutDateObj ? <>{signOutDateObj.toLocaleTimeString('en-AU', timeFormatOptions)}{v.autoSignOut && <span className="ml-1 text-xs italic text-gray-400">(Auto)</span>}</> : 'Not Signed Out'}
                                </td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">{formatStatus(v.status)}</td>
                                <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                    {v.status === 'signedOut' && (
                                        <button
                                            onClick={() => handleSignInAgain(v)}
                                            className="px-3 py-1 text-sm font-semibold rounded-md"
                                            style={{ backgroundColor: 'transparent', color: theme.blueDark, border: `1px solid ${theme.blueDark}` }}
                                        >
                                            Sign Back In
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default App;